import * as Sentry from '@sentry/react';
import type { PeerMetadata, SignalingGenerationV1 } from '@playcastdotio/Messaging';
import type {
  IPeerConnection,
  IPeerConnectionFactory,
  PeerConnectionOptions,
  SdpBitrateConfig,
  StreamEncodeContext,
  StreamResolution,
  VideoCodecFamily,
  WebRtcOptimizationConfig,
} from '@playcastdotio/PeerCore';
import {
  SdpInspector,
  SourceCadenceSampler,
  WebRtcOptimizationManager,
  configFromMetadata,
  createSdpBitrateTransform,
  sdpTransformConfigFromOptimization,
  IceHealthGuard,
  SdpGatherBuffer,
  classifyImplementationName,
  SoftwareEncodeRecoveryController,
  STARTUP_BURST_MS,
  shouldSkipStartupBurst,
  HOST_PLAYER_RECONNECT_HOLD_MS,
  GUEST_ICE_DISCONNECT_GRACE_MS,
  GUEST_TOTAL_RECOVERY_BUDGET_MS,
  type HardwareCodecProbeResult,
  type BeaconE2eSnapshot,
  type BitratePhase,
  type SoftwareEncodeRecoveryState,
  type HardwareAccelerationKind,
  type SendCodecDecision,
  SENDER_PIN_RETRY_WINDOW_MS,
  SOFTWARE_ENCODE_CONFIRM_SAMPLES,
  SOFTWARE_ENCODE_POLICY,
  TARGET_FRAMERATE,
  senderFrameRateForCapture,
  isDebugWebrtcLoggingEnabled,
  videoCodecFamilyOf,
  steadySendBandBps,
  FrameLatencyTracker,
  HostInputPhotonTagger,
  parseLatencyProbeMessage,
  type DecodeLagTierChange,
  isStreamHealthMessage,
  isStandingLatencyDrainResult,
  LatencyBudgetMonitor,
  StandingLatencyDetector,
  StickyDowngradePolicy,
  SessionQualityLedger,
  classifyLatencyBudget,
  capabilityForEngine,
  sdpNegotiatesPlayoutDelay,
  separateAudioMsid,
  usesAudioOpusPolicy,
  usesLowLatencyPlayout,
  RECOVER_PATH_MESSAGE,
  signalAvailabilityFor,
  type GuestBrowserEngine,
  type GuestLinkDebugSnapshot,
  type KeyframeReason,
  type RecoveryDemotionDecision,
  type StreamHealthEvent,
  type CalibratedLatencySummary,
  PeerMediaPolicyLifecycleManager,
} from '@playcastdotio/PeerCore';
import type { PeerType } from '../types';
import { PeerRecoveryComposition } from './mobileRecovery/PeerRecoveryComposition';
import type { StreamHealthManager } from '../observability/StreamHealthManager';
import { JitterBufferDiagnosticsManager } from './JitterBufferDiagnosticsManager';
import { KeyframeGate, type KeyframeRequestSource } from './KeyframeGate';
import { watchHostWindowTransitions } from './HostWindowTransitionGuard';
import { PeerTrackReplacementManager } from './PeerTrackReplacementManager';
import { VideoTrackSwapTransaction } from './trackReplacement/VideoTrackSwapTransaction';
import type { TrackSwapTarget, VideoTrackSwapContext } from './trackReplacement/trackSwapContracts';
import { LinkTroubleLedger } from './LinkTroubleLedger';
import { LatencyProbeManager } from './LatencyProbeManager';
import { SignalQualityPublisher } from './SignalQualityPublisher';
import { StandingLatencyDrainManager } from './StandingLatencyDrainManager';
import type {
  QualityTierDemotionHandler,
} from './RecoveryCapacityGate';
import { GuestFeedbackPathRestart, HOST_PATH_RESTART_DELAY_MS } from './GuestFeedbackPathRestart';
import { attachLiveVideoTrack, IdleVideoSenderWatch, type AttachResult } from './LiveCaptureAttacher';
import { sinkWebrtcDebugLine } from './webrtcDebugSink';
import { sinkStreamHealthLine } from '../observability/streamHealthSink';
import { PeerResolutionManager } from './mobileRecovery/PeerResolutionManager';
import { MobileSessionConfiguration, type MobileSessionOptions } from './mobileRecovery/MobileSessionConfiguration';
import { RtcSelectedPathReader } from '../../../../../../../libs/PeerCore/src/measurement/RtcSelectedPathReader';
import { VideoSenderSuspension } from '../../../../../../../libs/PeerCore/src/VideoSenderSuspension';

export type { KeyframeRequestSource } from './KeyframeGate';

/** Host e2e snapshot fields beyond PeerCore BeaconE2eSnapshot base. */
export type HostBeaconE2eSnapshot = BeaconE2eSnapshot & {
  mobileQuality?: {
    climbGateLocked?: boolean;
    initialWidth?: number; initialHeight?: number; senderWidth?: number; senderHeight?: number;
    finalWidth?: number; finalHeight?: number; qualifyingEvidenceMs: number; decisionAfterQualificationMs?: number;
    hostVerificationMs?: number; receiverConfirmationMs?: number; evidenceAgeMs?: number;
    guestEngine?: GuestBrowserEngine; deviceClass?: 'mobile' | 'desktop' | 'unknown';
    resolutionFallbackUnavailableReason?: string;
    result: 'verified' | 'decision-late' | 'host-failed' | 'receiver-unverified' | 'condition-not-produced' | 'unavailable';
  };
  encodeKind?: HardwareAccelerationKind;
  softwareEncodeRecoveryState?: SoftwareEncodeRecoveryState;
  bitratePhase?: BitratePhase;
  recoveryCleared?: boolean;
  postClearClearedAtMs?: number;
  /** Glass-to-glass frame latency measured against the guest's decode reports. */
  frameLatencyLastMs?: number | null;
  frameLatencyP50Ms?: number | null;
  frameLatencyP95Ms?: number | null;
  frameLatencySampleCount?: number;
  /**
   * Calibrated glass-to-glass latency (datachannel handshake + report), once the offset is
   * trusted. Present whenever the calibrated path has produced a sample, on Safari as well
   * as Chromium - unlike the rVFC hint path above, which is Chromium-only.
   */
  calibratedLatencyLastMs?: number | null;
  calibratedLatencyP50Ms?: number | null;
  calibratedLatencyP95Ms?: number | null;
  calibratedLatencySampleCount?: number;
  latencyProbeOffsetTrusted?: boolean;
  calibratedLatencySource?: CalibratedLatencySummary['source'];
};

const DIAG = '[AUDIO-BUG-12009:PeerConnection]';

/**
 * DEBUG BUILD ONLY — verbose host-side WebRTC recovery logging.
 *
 * The shipping app keeps this off (never constructed with it); the debug build turns it
 * on so a field session can be replayed end-to-end: host stats poll inputs, recovery
 * triggers with their reasons, applied encodings, and every signal-quality level written
 * to the guest. Always on in development-mode builds (vite-injected PLAYCAST_DEBUG_WEBRTC
 * =1 / `armWebrtcDebugLogging()` in the app entry) and under `?debug`. The flag is read
 * LIVE at each use so the app entry's arm (which runs after module load) still engages it.
 */

/**
 * A newer offer replaced this peer's connection while an earlier create was still
 * awaiting. Callers should abandon the attempt: the newer offer owns the peer now.
 */
export class ConnectionSupersededError extends Error {
  constructor(public readonly peerId: string) {
    super(`Connection for ${peerId} was superseded by a newer offer`);
    this.name = 'ConnectionSupersededError';
  }
}

/**
 * Chromium accepts a second setParameters() argument that forces an IDR on the next frame.
 * It is absent from the standard RTCRtpSender lib type and unavailable on other engines,
 * so senders are narrowed through a guard rather than asserted into shape.
 *
 * The parameters are typed as the base RTCRtpParameters because @types/webrtc merges its
 * own `getParameters(): RTCRtpParameters` overload into RTCRtpSender, so what a sender
 * hands back here is the base shape rather than lib.dom's RTCRtpSendParameters.
 */
type KeyframeCapableSender = {
  getParameters(): RTCRtpSendParameters;
  setParameters(parameters: RTCRtpParameters, options: { encodingOptions: { keyFrame: boolean }[] }): Promise<void>;
};

function supportsKeyframeRequest(sender: RTCRtpSender): sender is RTCRtpSender & KeyframeCapableSender {
  return typeof sender.setParameters === 'function';
}

/**
 * `lowLatencyPlayout` is the ENGINE GATE (libs/PeerCore/src/playoutDelay.ts): true for a
 * Chromium guest, false for Safari / Firefox, undefined until the peer's metadata is known.
 * `audioOpusPolicy` is separate: Safari gets the 20 ms Opus rewrite; Firefox does not.
 */
type SdpBitrateBinding = { config?: SdpBitrateConfig; lowLatencyPlayout?: boolean; audioOpusPolicy?: boolean };

/** Pull every SDP bitrate hint (kbps) down under a bps ceiling, keeping start/min ≤ max. */
function clampSdpBitrateToCeiling(hints: SdpBitrateConfig | undefined, ceilingBps: number): SdpBitrateConfig | undefined {
  if (!hints) return undefined;
  const maxKbps = Math.max(1, Math.round(ceilingBps / 1000));
  return {
    maxBitrate: maxKbps,
    startBitrate: Math.min(hints.startBitrate, maxKbps),
    minBitrate: Math.min(hints.minBitrate, maxKbps),
  };
}

export type PeerConnectionInfo = {
  peerId: string;
  peerType: PeerType;
  metadata: PeerMetadata;
  connection: IPeerConnection;
  optimizationConfig: WebRtcOptimizationConfig;
};

export type PeerConnectionManagerCallbacks = {
  onPeerConnected: (peerId: string, peerType: PeerType, metadata: PeerMetadata) => void;
  onPeerDisconnected: (peerId: string, peerType: PeerType) => void;
  onSignalGenerated: (peerId: string, description: RTCSessionDescriptionInit) => void;
  onIceCandidatesGenerated: (peerId: string, candidates: RTCIceCandidate[]) => void;
  onRemoteStream: (peerId: string, stream: MediaStream) => void;
  onDataChannelMessage: (peerId: string, data: string | ArrayBuffer) => void;
  onHardwareCodecStatus?: (
    peerId: string,
    result: HardwareCodecProbeResult,
    softwareEncodeRecoveryState: SoftwareEncodeRecoveryState,
  ) => void;
  /**
   * Signal quality for the guest HUD: the player-visible (calibrated glass-to-glass) latency
   * mapped onto the bar tiers. Return false when the level could not be delivered, so the
   * publisher retries it once the channel opens.
   */
  onSignalQuality?: (peerId: string, level: import('@playcastdotio/PeerCore').StreamSignalQualityLevel) => boolean | void;
  /**
   * Hardware encode is unreachable for this peer and escalation is exhausted. The session
   * must be ended — software encode is never an acceptable end state.
   */
  onSoftwareEncodeRefused?: (peerId: string, encoderImplementation?: string) => void;
  /** Fired when a reconnect hold expires without guest reclaim. */
  onReconnectHoldExpired?: (peerId: string) => void;
  /** Fired when a reconnect hold is armed (extends or starts reclaim window). */
  onReconnectHoldArmed?: (peerId: string) => void;
  /**
   * Fired when the host recovers a peer by ICE restart because no guest reclaim arrived.
   * No offer is renegotiated on that path, so quality-tier re-evaluation has to be driven
   * from here rather than from the incoming-offer handler.
   */
  /**
   * A player peer's transport has gone bad, or the host gave up waiting for its reclaim.
   * The coordinator re-evaluates the shared camera tier — this is what steps a struggling
   * mobile guest down to the 360p capture.
   */
  onPlayerTransportTrouble?: (peerId: string, metadata: PeerMetadata) => void;
  /**
   * Resolution of the ShadowCam capture currently feeding every encoder, or null when it
   * cannot be determined. The capture is shared, so this — not a peer's own tier hint — is
   * what the congestion cuts have to be sized against.
   */
  getCaptureResolution?: () => StreamResolution | null;
  /** Frame rate of the live capture, or null when the track has not reported one. */
  getCaptureFrameRate?: () => number | null;
  /** Native sender cadence; null means native owns video but its fresh measurement is unavailable. */
  getNativeEncodeFps?: (peerId: string) => number | null | undefined;
  /**
   * Fired when a replacement peer connection is registered (before ICE/data channel ready).
   * Used to cancel deferred player count changes that would otherwise fire before the
   * reclaiming peer becomes fully connected.
   */
  onPeerConnectionRegistered?: (peerId: string, peerType: PeerType) => void;
  /**
   * Fired once per decode-lag episode when the host applies the 360p demotion.
   * PeerCoordinator runs the real shared-capture swap to ShadowCam00.
   * Optional so Phase 3 (PCM) and Phase 4 (coordinator) can land independently.
   */
  onDecodeLagTierDemotion?: QualityTierDemotionHandler;
  /**
   * Guest audioLevel/bitrate sample for the silent-but-attached watchdog (AUDIO-BUG-12009).
   * Observability+recovery lives in PeerCoordinator so PCM stays free of MediaCoordinator.
   */
  onGameAudioHealthSample?: (
    peerId: string,
    sample: { audioLevel?: number; audioBitrateBps?: number },
  ) => void;
  /**
   * ICE is up and the video encoder has not started. The coordinator attaches the
   * live capture; a sender that already has it needs an ICE restart instead.
   */
  onVideoSenderIdle?: (peerId: string) => void;
  /**
   * Host wall time at which the native WebCodecs sender stamped `sentUs` on a frame. The
   * calibrated latency probe asks this for `webcodecs-sent` reports, where no insertable-streams
   * send record exists; PeerCoordinator answers from `NativeVideoHost`.
   */
  getNativeSendWallMs?: (peerId: string, sentUs: number) => number | undefined;
};

/**
 * PeerConnectionManager - Manages WebRTC peer connections via @bowbee/peer-lite.
 *
 * Uses injectable IPeerConnectionFactory for testability.
 * Host-side: manages Map<peerId, IPeerConnection>, each remote peer gets polite=true.
 * Client-side: single connection with polite=false.
 */
export class PeerConnectionManager {
  private connections = new Map<string, PeerConnectionInfo>();
  private readonly mediaPolicyByPeer = new Map<string, PeerMediaPolicyLifecycleManager>();
  private readonly captureGenerationByPeer = new Map<string, number>();
  private nextCaptureGeneration = 0;
  private readonly trackReplacementManager = new PeerTrackReplacementManager();
  private swapLifecycle = 0;
  private swapSequence = 0;
  private readonly swapGenerations = new WeakMap<IPeerConnection, number>();
  private readonly initialAnswererNegotiation = new WeakMap<IPeerConnection, () => void>();
  private readonly videoTrackSwap = new VideoTrackSwapTransaction({
    nowMs: () => Date.now(),
    schedule: (callback, delay) => { const timer = setTimeout(callback, delay); return () => clearTimeout(timer); },
    targets: () => this.trackSwapTargets(),
    isCurrentTarget: target => this.connections.get(target.peerId)?.connection === target.connection,
    replace: (target, from, to) => this.trackReplacementManager.replace(target.connection, from, to, () => undefined),
    committed: target => {
      const info = this.connections.get(target.peerId);
      if (info?.connection !== target.connection) return;
      this.captureGenerationByPeer.set(info.peerId, ++this.nextCaptureGeneration);
      this.reprimeSenderParametersAndKeyframe(info);
    },
    recoverFailedTarget: target => this.destroyConnectionForFailedTrackSwap(target.peerId, target.connection),
  });
  private factory: IPeerConnectionFactory;
  private callbacks: PeerConnectionManagerCallbacks;
  private defaultIceConfig: RTCConfiguration = {};
  private optimizationManager = new WebRtcOptimizationManager();
  private sdpInspector = new SdpInspector();
  private bitrateOverride: number | null = null;
  private preWarmedConnection: IPeerConnection | null = null;
  private preWarmCreatedAt = 0;
  /**
   * Binding owned by the currently pre-warmed connection. A fresh object per pre-warm,
   * handed to the consuming peer at consume time: a single shared object would let the
   * next peer's config overwrite the one a live pre-warmed connection is still using.
   */
  private preWarmSdpBinding: SdpBitrateBinding | null = null;
  private readonly sdpBitrateBindings = new Map<string, SdpBitrateBinding>();
  private static readonly PREWARM_MAX_AGE_MS = 60_000;
  private static readonly HOST_ANSWER_GATHER_TIMEOUT_MS = 500;
  /**
   * Holds ICE candidates that arrive before setRemoteDescription has resolved.
   * Drained via drainPendingIceCandidates() after signal(sdp) completes.
   * Prevents the InvalidStateError (HOST-FRONTEND-Z) caused by addIceCandidate
   * racing against setRemoteDescription in the concurrent signaling handler.
   */
  private pendingIceCandidates = new Map<string, RTCIceCandidate[]>();
  /** Buffers host answer SDP until relay candidates bake in (or timeout). */
  private hostAnswerBuffers = new Map<string, SdpGatherBuffer>();
  private congestionRecovery: PeerRecoveryComposition;
  /** Guest HUD signal bars, published from the calibrated (player-visible) latency. */
  private readonly signalQuality: SignalQualityPublisher;
  /** PRODUCT DECISION D1 — sticky one-way downgrade floor, shared with the recovery manager. */
  private readonly stickyDowngrade = new StickyDowngradePolicy();
  /** PRODUCT DECISION D2 — per-peer latency-budget breach detector. */
  private readonly latencyBudget = new LatencyBudgetMonitor();
  /** Session-quality ledger for sticky upgrade violation oracles (Phase 6). */
  private readonly qualityLedger = new SessionQualityLedger();
  /** Names a standing receive-side buffer: detector + [JitterBuffer] lines + standing-buffer / resolution-switch events. */
  private readonly jitterBufferDiagnostics = new JitterBufferDiagnosticsManager({
    detector: new StandingLatencyDetector(),
    emitHealthEvent: (peerId, kind, detail) => this.streamHealth?.recordHostEvent(peerId, { kind, stream: 'video', atMs: Date.now(), detail }),
    debugLog: (tag, fields) => sinkWebrtcDebugLine(JSON.stringify({ t: new Date().toISOString(), tag, ...fields })),
    onStandingTransition: (peerId, verdict, transition, depth) => this.standingLatencyDrain.onStandingTransition(peerId, verdict, transition, depth),
  });
  /** The engine each guest reports on clientStats, so engine-aware policy keys on the real guest. */
  private readonly guestEngineByPeer = new Map<string, GuestBrowserEngine>();
  private readonly standingLatencyDrain = new StandingLatencyDrainManager({
    send: (peerId, json) => this.connections.get(peerId)?.connection.send(json) ?? false,
    applyEncoding: (peerId, opts) => this.applyRecoveryEncoding(peerId, opts),
    currentEncoding: (peerId) => this.congestionRecovery.getSnapshot(peerId),
    emitHealthEvent: (peerId, kind, detail) => this.streamHealth?.recordHostEvent(peerId, { kind, stream: 'video', atMs: Date.now(), detail }),
    debugLog: (tag, fields) => sinkWebrtcDebugLine(JSON.stringify({ t: new Date().toISOString(), tag, ...fields })),
  });
  /** Always-on stream-health bus, injected by the coordinator (Phase 3 backbone). */
  private streamHealth?: StreamHealthManager;
  private statsPollTimers = new Map<string, ReturnType<typeof setInterval>>();
  /** Last cumulative packetsLost per peer for delta computation. */
  private lastPacketsLost = new Map<string, number>();
  /** Last cumulative packetsSent (outbound video) per peer for loss %. */
  private lastPacketsSent = new Map<string, number>();
  /** Last cumulative packetsReceived (remote-inbound video) per peer for loss %. */
  private lastPacketsReceived = new Map<string, number>();
  /**
   * Consecutive 1Hz polls where Admin encode is dead (encodeFps null/0 and no packets).
   * Used to fire a once-per-session `[ADMIN_CONNECT] encode-dead` always-on diagnostic.
   */
  private adminEncodeDeadTicks = new Map<string, number>();
  /** peerIds that already alerted for the current dead-encode episode (cleared when healthy). */
  private adminEncodeDeadAlerted = new Set<string>();
  /**
   * Previous cumulative send-delay counters per peer, keyed by outbound-rtp stat id.
   *
   * Keyed per stat rather than per peer because a peer can have several concurrent
   * outbound video streams, and its stat ids change after renegotiation or a track
   * replace. A single peer-level baseline lets one stream consume another's previous
   * values, which yields deltas that describe neither stream.
   */
  private lastSendDelaySamples = new Map<string, Map<string, { totalSendDelay: number; framesEncoded: number }>>();
  /** Last classified outbound encode kind per peer. */
  private lastHardwareEncodeKind = new Map<string, HardwareAccelerationKind>();
  /** Last (encodeKind + recoveryState) key emitted to UI callbacks. */
  private lastHardwareCodecEmitKey = new Map<string, string>();
  private lastAnswerSdpByPeer = new Map<string, string>();
  private idrRequestCountByPeer = new Map<string, number>();
  private lastRttByPeer = new Map<string, number>();
  private lastLossPctByPeer = new Map<string, number>();
  /** Every IDR passes through here: client rate limit, engine-aware withholding, the `[Keyframe]` record. */
  private readonly keyframeGate: KeyframeGate;
  private readonly stopWindowWatch: () => void;
  /** Recent link record per peer; outlives the connection so a recovery can ask whether the LINK failed. */
  private readonly linkTrouble = new LinkTroubleLedger();
  /** Most recently touched peer for e2e snapshot selection. */
  private e2eActivePeerId: string | undefined;
  private softwareEncodeRecoveryByPeer = new Map<string, SoftwareEncodeRecoveryController>();
  /**
   * H.264 profiles observed encoding in SOFTWARE for this peer, by sdpFmtpLine.
   *
   * Per-peer rather than per-host on purpose: hardware encoders have concurrent-session
   * limits, so a profile that ran in hardware for the first player can legitimately fall
   * back to software for the third.
   */
  /** Consecutive polls reporting software encode, per peer. Reset by any hardware report. */
  private consecutiveSoftwareSamples = new Map<string, number>();
  /** Last logged (codec, profile, implementation) tuple, so the diagnostic logs on change. */
  private lastEncodeDiagnosticKey = new Map<string, string>();
  /** Negotiated outbound video codec per peer, e.g. `video/H264 42e01f`. Diagnostic only. */
  private negotiatedVideoCodecByPeer = new Map<string, string>();
  /** Peers whose offered H.264 profile list has already been logged. */
  private loggedOfferProfiles = new Set<string>();
  /** H.264 profiles the guest OFFER carried, for the overlay - the negotiated ceiling. */
  private offeredProfilesByPeer = new Map<string, string[]>();
  /** Compact result of the last send-codec pin attempt, for the overlay. */
  private pinSummaryByPeer = new Map<string, string>();
  /**
   * Latches terminal codec refusal so retries cannot emit duplicate teardown actions.
   * A pin that is still retry-pending is re-attempted until its window expires.
   */
  private codecRefusedPeers = new Set<string>();
  /** When a retry-pending pin first went pending, per peer. */
  private codecPinPendingSinceMs = new Map<string, number>();
  /** Last non-empty encoder implementation seen, so refreshes do not blank the overlay. */
  private lastEncoderImplByPeer = new Map<string, string>();
  private bitratePhaseByPeer = new Map<string, BitratePhase>();
  /** When set, peers that arrive after this instant attach at the settled bitrate. */
  private pipelineSettledAtMs: (() => number | null) | null = null;
  private bitratePhaseTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private static readonly STATS_POLL_MS = 1_000;
  /** Peers being replaced during reconnect — suppress disconnect callback emission. */
  private reconnectingPeerIds = new Set<string>();
  /** Hold timers so last-player disconnect does not tear down before guest reclaim. */
  private reconnectHoldTimers = new Map<string, ReturnType<typeof setTimeout>>();
  /**
   * Per-peer glass-to-glass frame latency tracker: send timestamps from the video
   * sender's encoded frames, correlated with the guest's reported decode times.
   */
  private frameLatencyTrackers = new Map<string, FrameLatencyTracker>();
  /**
   * Names the first encoded frame after each sampled guest input, so the guest can measure
   * input-to-photon on its own clock. The host contributes only the correlation key here —
   * no host timestamp enters that measurement.
   */
  private readonly inputPhotonTagger = new HostInputPhotonTagger();
  /** Calibrated latency probe (ping cadence, offset, report) — extracted from this manager. */
  private readonly latencyProbe: LatencyProbeManager;
  /**
   * Decode-lag episode state that must survive reclaim / ICE restart (cleared only with
   * the sticky session on hold expiry or host-initiated end).
   */
  private decodeLagAppliedFps = new Map<string, number>();
  /**
   * Last MEASURED outbound-rtp `framesPerSecond` per peer — what the encoder actually put on
   * the wire, as opposed to what it was configured to send. The denominator for every
   * decode-side judgement; see `CongestionRecoveryDeps.getDeliveredFps`.
   */
  private deliveredFpsByPeer = new Map<string, number>();
  private decodeLagCutBitrateBps = new Map<string, number>();
  private decodeLagDemotionFired = new Set<string>();
  private readonly peerResolution: PeerResolutionManager;
  private readonly mobileSessionConfig: MobileSessionConfiguration;
  private readonly selectedPathReader = new RtcSelectedPathReader();
  /** Timestamp (ms) of the last remote OFFER applied per peer — signals a guest reclaim. */
  private lastRemoteOfferAtByPeer = new Map<string, number>();
  /** SDP session ID of the last offer from each peer — used to detect ICE restart vs full rebuild. */
  private lastOfferSessionIdByPeer = new Map<string, string>();
  /** Pending deferred host ICE-restart timers (Phase 2 defer-to-guest-reclaim). */
  private iceRestartDeferTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private readonly guestFeedbackPathRestart = new GuestFeedbackPathRestart();
  private readonly idleVideoSender = new IdleVideoSenderWatch();
  /** False while WebCodecs owns video: a silent Electron sender is the selected path, not a stuck encoder. */
  private idleVideoRepairEnabled = true;
  private pathRecoverTimers = new Map<string, ReturnType<typeof setTimeout>>();
  /** Player peers whose teardown is held open while their transport tries to come back. */
  private playerIceGraceTimers = new Map<string, ReturnType<typeof setTimeout>>();
  /**
   * Phase 2: how long the host waits for a guest's coordinated full re-offer
   * (reclaim) before falling back to a host-initiated ICE restart. Kept well
   * under HOST_PLAYER_RECONNECT_HOLD_MS so recovery still completes in-window.
   *
   * Must be longer than the guest's whole recovery budget (GUEST_RECOVERY_BUDGET_MS,
   * ~4s) plus signaling latency margin, but shorter than HOST_PLAYER_RECONNECT_HOLD_MS
   * (8s). The guest hard-stops reclaiming after GUEST_RECOVERY_DEADLINE_MS (3s), so
   * waiting longer than the hold would keep a failed peer alive long after the guest
   * has abandoned recovery, delaying teardown and onPlayerTransportTrouble.
   *
   * That the guest's budget is now a real bound is what makes this sizeable at all. Its
   * pre-offer waits - the realtime websocket-ready gate especially, which can sit for tens
   * of seconds on a cellular failover - are raced against the same budget, so a guest that
   * has not re-offered by then has already given up and moved to a full refresh. Sized
   * against that wait unbounded, this had to be 22s, which fired long after the reconnect
   * hold had lapsed and restarted a connection nobody was waiting on.
   *
   * Sized against the guest's TOTAL budget (including its ICE-disconnect grace): the grace
   * runs BEFORE the guest's own budget clock starts, so a guest that spends the whole grace
   * and then the whole budget can legitimately still be reclaiming at grace+budget. Deferring
   * narrower than that lets the host restart ICE while a still-valid guest reclaim is on the
   * wire - and because the host restart is a NEW offer with a new ICE session, the guest
   * reads it as a stranger re-offer, drops the reclaim, and the session tears down for
   * nothing. The defer must at least cover grace+budget; the reconnect hold
   * (HOST_PLAYER_RECONNECT_HOLD_MS) is the outer bound on both.
   */
  private static readonly HOST_ICE_RESTART_DEFER_MS = Math.min(
    GUEST_TOTAL_RECOVERY_BUDGET_MS + GUEST_ICE_DISCONNECT_GRACE_MS + 500,
    HOST_PLAYER_RECONNECT_HOLD_MS,
  );

  /**
   * How long the host tolerates a player's ICE `disconnected` before tearing the connection
   * down — the counterpart to the guest's own blip grace.
   *
   * The two ends used to disagree about what a disconnect meant. The guest replaces peer-lite's
   * ICE handler outright and rides out a blip for GUEST_ICE_DISCONNECT_GRACE_MS, trying an
   * in-place ICE restart partway through. The host delegated to peer-lite, which destroys the
   * RTCPeerConnection on the FIRST 'disconnected'. So whenever both ends saw the same drop, the
   * guest spent its whole grace attempting to repair a peer connection the host had already
   * destroyed — a repair that could not possibly succeed — and only then started the reclaim
   * that was always going to be necessary. That was pure latency in front of every recovery.
   *
   * Derived from the guest's grace rather than chosen independently: the host must still be
   * holding the connection when the guest either recovers in place or gives up and reclaims,
   * and deriving it is what stops the two from drifting apart again. The margin covers the
   * round trip of a reclaim offer sent right at the guest's deadline.
   *
   * The host deliberately does NOT restart ICE itself during this window. The guest is running
   * its own restart, and two ends renegotiating at once is glare — the same reason
   * deferHostIceRestart exists. The host's job here is simply to still be there.
   */
  private static readonly HOST_PLAYER_ICE_GRACE_MS = GUEST_ICE_DISCONNECT_GRACE_MS + 1_000;
  /**
   * Connections being destroyed by the host (kick, stream stop, destroyAll).
   * The 'disconnected' event should NOT arm a reconnect hold for these because
   * the guest has no opportunity to reclaim — the host ended the session.
   */
  private hostInitiatedDestroys = new Set<string>();

  /**
   * Receiver feedback stopped while this peer is still up. Ask the guest to restart, then
   * restart from this side if the guest never answers. A data-channel ask cannot reach a
   * guest whose data channel is what died, and a tier cut would not bring the picture back.
   */
  private rebuildPathAfterGuestFeedbackLoss(peerId: string): void {
    if (this.hostInitiatedDestroys.has(peerId)) return;
    const entry = this.connections.get(peerId);
    if (!entry) return;
    const sent = entry.connection.send(JSON.stringify(RECOVER_PATH_MESSAGE));
    const askedAt = Date.now();
    this.guestFeedbackPathRestart.noteFeedbackLost(peerId, askedAt);
    sinkWebrtcDebugLine(JSON.stringify({
      t: new Date().toISOString(),
      tag: '[Path.recover]',
      peerId,
      action: 'ask-guest',
      sent,
    }));
    this.armHostPathRestart(peerId, askedAt);
  }

  private armHostPathRestart(peerId: string, askedAt: number): void {
    this.clearPathRecoverTimer(peerId);
    const timer = setTimeout(() => {
      this.pathRecoverTimers.delete(peerId);
      this.restartPathIfGuestStayedSilent(peerId, askedAt);
    }, HOST_PATH_RESTART_DELAY_MS);
    this.pathRecoverTimers.set(peerId, timer);
  }

  private restartPathIfGuestStayedSilent(peerId: string, askedAt: number): void {
    if (this.hostInitiatedDestroys.has(peerId) || !this.connections.has(peerId)) {
      this.guestFeedbackPathRestart.clear(peerId);
      return;
    }
    const iceState = this.connections.get(peerId)?.connection.getRtcPeerConnection()?.iceConnectionState;
    const remoteOfferAtMs = this.lastRemoteOfferAtByPeer.get(peerId) ?? 0;
    const nowMs = Math.max(Date.now(), askedAt + HOST_PATH_RESTART_DELAY_MS);
    if (!this.guestFeedbackPathRestart.consumeRestart(peerId, nowMs, iceState, remoteOfferAtMs)) {
      sinkWebrtcDebugLine(JSON.stringify({
        t: new Date().toISOString(),
        tag: '[Path.recover]',
        peerId,
        action: 'skip',
        iceState: iceState ?? 'unknown',
      }));
      return;
    }
    sinkWebrtcDebugLine(JSON.stringify({
      t: new Date().toISOString(),
      tag: '[Path.recover]',
      peerId,
      action: 'host-ice-restart',
      iceState: iceState ?? 'unknown',
      askedAt,
    }));
    void this.tryIceRestart(peerId);
  }

  private clearPathRecoverTimer(peerId: string): void {
    const timer = this.pathRecoverTimers.get(peerId);
    if (!timer) return;
    clearTimeout(timer);
    this.pathRecoverTimers.delete(peerId);
  }

  constructor(factory: IPeerConnectionFactory, callbacks: PeerConnectionManagerCallbacks, options: MobileSessionOptions = {}) {
    this.factory = factory;
    this.callbacks = callbacks;
    this.mobileSessionConfig = new MobileSessionConfiguration(options);
    this.peerResolution = new PeerResolutionManager({ nowMs: () => Date.now(),
      guestWallTimeBounds: (peerId, guestWallMs, epoch) => this.latencyProbe.guestWallTimeBounds(peerId, guestWallMs, epoch),
      onSenderVerified: peerId => this.decodeLagDemotionFired.add(peerId),
      emit: (peerId, request, status) => {
        const detail = { ...status, peerGeneration: request.peerGeneration, pathEpoch: request.pathEpoch,
          maxWidth: request.maxWidth, maxHeight: request.maxHeight };
        this.streamHealth?.recordHostEvent(peerId, { kind: 'quality-change-stage', stream: 'video', atMs: Date.now(), detail: { ...status } });
        sinkWebrtcDebugLine(JSON.stringify({ t: new Date().toISOString(), tag: '[Resolution.stage]', peerId, ...detail }));
      },
    });
    this.signalQuality = new SignalQualityPublisher({
      send: (peerId, level) => this.callbacks.onSignalQuality?.(peerId, level),
    });
    this.latencyProbe = new LatencyProbeManager({
      send: (peerId, message) =>
        this.connections.get(peerId)?.connection.send(JSON.stringify(message)) ?? false,
      nowMs: () => Date.now(),
      monotonicNowMs: () => performance.now(),
      getHostSendWallMs: (peerId, frameKey, source) =>
        source === 'webcodecs-sent'
          ? this.callbacks.getNativeSendWallMs?.(peerId, frameKey)
          : this.frameLatencyTrackers.get(peerId)?.getSendWallMs(frameKey),
      debugSink: (line) => {
        if (isDebugWebrtcLoggingEnabled()) {
          console.log(line);
          sinkWebrtcDebugLine(line);
        }
      },
      // Every trusted calibrated sample is what the guest's signal bars read.
      onCalibratedSample: (peerId, calibratedMs) =>
        this.signalQuality.publishLatency(peerId, calibratedMs),
    });
    this.keyframeGate = new KeyframeGate({
      engineFor: (peerId) => this.guestEngineByPeer.get(peerId),
      guestLinkFor: (peerId) => this.jitterBufferDiagnostics.guestLink(peerId),
      // Console for the live session, NDJSON so the keyframe sits beside the [JitterBuffer] tick it may have moved.
      log: (tag, fields) => {
        console.log(tag, fields);
        if (isDebugWebrtcLoggingEnabled()) sinkWebrtcDebugLine(JSON.stringify({ t: new Date().toISOString(), tag, ...fields }));
      },
    });
    this.congestionRecovery = new PeerRecoveryComposition({
      resolution: this.peerResolution,
      latency: this.latencyProbe,
      sourceCadenceFactory: { create: () => new SourceCadenceSampler() },
      recovery: {
      applyEncoding: (peerId, opts) => this.applyRecoveryEncoding(peerId, opts),
      requestKeyframeOnce: (peerId, reason, framesDropped) =>
        this.requestKeyframeForPeer(peerId, { reason, framesDropped }),
      requestImmediateRecoveryKeyframe: (peerId) => {
        this.keyframeGate.armImmediateRecovery(peerId, 'confirmed-stall');
        this.requestKeyframeForPeer(peerId, { source: 'client', reason: 'client' });
      },
      getProfileMaxBitrateBps: (peerId) => this.steadyMaxBitrateBps(peerId),
      getEncodeContext: (peerId) => this.encodeContextForPeer(peerId),
      getCalibratedLatency: (peerId) => {
        const s = this.getCalibratedLatencySummary(peerId);
        // estimate source never feeds the climb gate or the budget (Phase 3 / 5).
        if (s.source === 'estimate') return undefined;
        // The reading travels with the link's own floor: only the excess above it is
        // actionable, and without a trusted floor the budget and gate stay silent.
        return { lastMs: s.lastMs, floorMs: s.floorMs };
      },
      // The decode-lag downgrade: resolve the live capture tier + fps, and apply the change
      // (switch the guest to the 360p tier + the fps step). The tier/fps resolution reads the
      // live encode context so a shared capture (ShadowCam) is respected. When the capture is
      // unknown the tier falls back to the mobile default (middle) - the downgrade is to the
      // bottom tier regardless of what the peer's current resolution reads as.
      getCurrentTier: (peerId) => this.currentQualityTierForPeer(peerId),
      getCurrentFps: (peerId) => {
        const applied = this.decodeLagAppliedFps.get(peerId);
        if (applied !== undefined) return applied;
        const ctx = this.encodeContextForPeer(peerId);
        return ctx?.frameRate ?? TARGET_FRAMERATE;
      },
      getDeliveredFps: (peerId) => this.deliveredFpsForPeer(peerId),
      applyDecodeLagTierChange: (peerId, change) => {
        this.applyDecodeLagTierChange(peerId, change);
      },
      resolutionFallbackUnavailableReason: 'resolution-fallback-disabled',
      onPathChanged: (peerId, epoch) => {
        this.frameLatencyTrackers.get(peerId)?.reset();
        this.lastSendDelaySamples.delete(peerId);
        this.lastPacketsReceived.delete(peerId);
        this.lastPacketsLost.delete(peerId);
        this.jitterBufferDiagnostics.clearPeer(peerId);
        this.standingLatencyDrain.clearPeer(peerId);
      },
      // PRODUCT DECISIONS D1 + D2: the sticky floor and the latency-budget monitor are
      // shared instances so the recovery manager reads the same state the coordinator
      // exposes. The health bus is looked up live (this.streamHealth) because the
      // coordinator injects it after construction.
      stickyDowngrade: this.stickyDowngrade,
      latencyBudget: this.latencyBudget,
      qualityLedger: this.qualityLedger,
      onHealthEvent: (peerId, event) => {
        this.streamHealth?.recordHostEvent(peerId, event);
        this.mirrorHealthEventToWebrtcDebug(peerId, event);
      },
      getStandingLatency: (peerId) => this.jitterBufferDiagnostics.currentVerdict(peerId),
      // A drain step in flight is not reclaim evidence: the encoding it dips is the drain's, not the link's.
      isDraining: (peerId) => this.standingLatencyDrain.isDraining(peerId),
      // Defence in depth over the guest's omit-don't-zero-fill contract: the ladder may only
      // decide on signals this guest's engine can measure. Unknown engine = no mask.
      getSignalAvailability: (peerId) => {
        const engine = this.guestEngineByPeer.get(peerId);
        return engine ? signalAvailabilityFor(capabilityForEngine(engine)) : undefined;
      },
      // The HUD's red bars are the product's own unplayable verdict — the same engine-
      // independent reading the guest displays must corroborate the recovery ladder.
      getSignalQualityLevel: (peerId) => {
        const level = this.signalQuality.levelFor(peerId);
        return level === 'none' ? null : level;
      },
      onGuestFeedbackLost: (peerId) => this.rebuildPathAfterGuestFeedbackLoss(peerId),
      },
      onFrameDecodeHints: (peerId, hints) => this.onClientFrameDecodeHints(peerId, hints),
      onClientStatsObserved: (peerId, msg, reportFreshness) => {
        this.guestFeedbackPathRestart.noteGuestAlive(peerId);
        this.clearPathRecoverTimer(peerId);
        if (msg.guestEngine) this.guestEngineByPeer.set(peerId, msg.guestEngine);
        const g2g = this.getCalibratedLatencySummary(peerId);
        this.jitterBufferDiagnostics.observe(peerId, msg, {
          deliveredFps: this.deliveredFpsForPeer(peerId) ?? undefined,
          g2gLastMs: g2g.lastMs,
          g2gFloorMs: g2g.floorMs,
          measurementFresh: reportFreshness.state === 'fresh' && g2g.freshness?.state === 'fresh',
          sendCodec: this.negotiatedVideoCodecByPeer.get(peerId),
          sinceKeyframeMs: this.keyframeGate.sinceKeyframeMs(peerId),
          keyframesWithheld: this.keyframeGate.withheldCount(peerId),
        });
        this.callbacks.onGameAudioHealthSample?.(peerId, {
          audioLevel: msg.audioLevel,
          audioBitrateBps: msg.audioBitrateBps,
        });
      },
    });
    this.stopWindowWatch = watchHostWindowTransitions(
      () => this.congestionRecovery.noteHostWindowTransition(),
    );
  }

  /**
   * What this peer is encoding right now: the live capture resolution and the send codec.
   *
   * Both have to be read live rather than taken from the peer's stored config. The ShadowCam
   * capture is SHARED — one mobile guest can pull the whole capture down to 360p — so a
   * desktop peer's own config would claim 720p while the encoder is fed 360p, and a bitrate
   * derived from it would be four times too high. The codec is read off the sender because
   * that is the pin that actually took effect, not the one that was requested.
   *
   * Returns undefined rather than guessing. Every caller falls back to the previous
   * fixed-bitrate behaviour, so an unknown context is never worse than before.
   */
  private encodeContextForPeer(peerId: string): StreamEncodeContext | undefined {
    const info = this.connections.get(peerId);
    if (!info) return undefined;

    let captured: ReturnType<NonNullable<PeerConnectionManagerCallbacks['getCaptureResolution']>> = null;
    try {
      captured = this.callbacks.getCaptureResolution?.() ?? null;
    } catch (error) {
      // An unreadable capture falls back to the peer's stored resolution. Throwing
      // here used to skip the bitrate cut entirely.
      console.warn('[PeerConnectionManager] capture resolution unreadable:', error);
    }
    const resolution = captured ?? this.getEffectiveOptimizationConfig(info).captureResolution;
    if (!resolution) return undefined;

    return {
      resolution,
      frameRate: senderFrameRateForCapture(
        this.callbacks.getCaptureFrameRate?.() ?? null,
        this.getEffectiveOptimizationConfig(info).targetFrameRate,
      ),
      codec: this.sendCodecFamilyFor(info),
    };
  }

  /** Family of the codec the video sender is actually using, if one is established. */
  private sendCodecFamilyFor(info: { connection: IPeerConnection }): VideoCodecFamily | undefined {
    const rtcPc = info.connection.getRtcPeerConnection();
    if (!rtcPc || typeof rtcPc.getSenders !== 'function') return undefined;
    for (const sender of rtcPc.getSenders()) {
      if (sender.track?.kind !== 'video') continue;
      const params = sender.getParameters() as RTCRtpSendParameters & {
        codecs?: Array<{ mimeType: string }>;
      };
      // The pinned codec on the encoding is the one in force; the negotiated list's first
      // entry is what would be used if nothing was pinned.
      const pinned = (params.encodings?.[0] as { codec?: { mimeType?: string } } | undefined)?.codec;
      const family = videoCodecFamilyOf(pinned?.mimeType ?? params.codecs?.[0]?.mimeType);
      if (family) return family;
    }
    return undefined;
  }

  public setIceConfig(config: RTCConfiguration): void {
    this.defaultIceConfig = config;
  }

  /**
   * Override the max video bitrate (in bps) for all current and future peers.
   * Pass null to revert to the per-peer defaults from configFromMetadata().
   */
  public setBitrateOverride(bitrateBps: number | null): void {
    this.bitrateOverride = bitrateBps;
    const label = bitrateBps !== null ? `${(bitrateBps / 1_000_000).toFixed(1)} Mbps` : 'default';
    console.log(`[PeerConnectionManager] Bitrate override set to ${label}`);

    for (const info of this.connections.values()) {
      const rtcPc = info.connection.getRtcPeerConnection();
      if (!rtcPc) continue;

      const effectiveConfig = this.getEffectiveOptimizationConfig(info);
      this.syncSdpBitrateBinding(info.peerId, effectiveConfig, info.metadata);
      this.applyEncodingRespectingRecovery(info.peerId, info, rtcPc, effectiveConfig);
    }
  }

  public getBitrateOverride(): number | null {
    return this.bitrateOverride;
  }

  private getEffectiveOptimizationConfig(info: PeerConnectionInfo): WebRtcOptimizationConfig {
    const config = this.withCapturedFrameRate(this.mobileSessionConfig.apply(this.withBitrateOverride(info.optimizationConfig),
      info.metadata.isMobile === true, this.peerResolution.hasRetainedFloor(info.peerId) || this.decodeLagDemotionFired.has(info.peerId)));
    const cap = this.congestionRecovery.getHandoffProtection(info.peerId);
    if (cap === undefined) return config;
    return { ...config, maxBitrate: Math.min(config.maxBitrate ?? Infinity, cap),
      burstMaxBitrate: Math.min(config.burstMaxBitrate ?? config.maxBitrate ?? Infinity, cap),
      sdpBitrate: clampSdpBitrateToCeiling(config.sdpBitrate, cap), burstSdpBitrate: clampSdpBitrateToCeiling(config.burstSdpBitrate, cap) };
  }

  /** The open capture's frame rate replaces the 60 fps fallback, and the picture budget scales with it. */
  private withCapturedFrameRate(config: WebRtcOptimizationConfig): WebRtcOptimizationConfig {
    const fps = senderFrameRateForCapture(this.callbacks.getCaptureFrameRate?.() ?? null, config.targetFrameRate);
    const baseline = config.targetFrameRate && config.targetFrameRate > 0 ? config.targetFrameRate : TARGET_FRAMERATE;
    if (!(fps > 0) || fps === baseline) return { ...config, targetFrameRate: fps };
    const scale = fps / baseline;
    const scaleHints = (hints: SdpBitrateConfig | undefined): SdpBitrateConfig | undefined => {
      if (!hints) return hints;
      return {
        ...hints,
        minBitrate: Math.max(1, Math.round(hints.minBitrate * scale)),
        startBitrate: Math.max(1, Math.round(hints.startBitrate * scale)),
        maxBitrate: Math.max(1, Math.round(hints.maxBitrate * scale)),
      };
    };
    return {
      ...config,
      targetFrameRate: fps,
      maxBitrate: config.maxBitrate !== undefined ? Math.round(config.maxBitrate * scale) : undefined,
      burstMaxBitrate: config.burstMaxBitrate !== undefined ? Math.round(config.burstMaxBitrate * scale) : undefined,
      sdpBitrate: scaleHints(config.sdpBitrate),
      burstSdpBitrate: scaleHints(config.burstSdpBitrate),
    };
  }

  /**
   * Apply the operator bitrate override as a true ceiling.
   *
   * Every bitrate the config can express has to come down together: lowering only the
   * max leaves start/min above it, which produces contradictory fmtp hints (start > max)
   * and lets the burst phase encode above the override the operator asked for.
   */
  private withBitrateOverride(base: WebRtcOptimizationConfig): WebRtcOptimizationConfig {
    if (this.bitrateOverride === null) return base;

    const ceilingBps = this.bitrateOverride;
    return {
      ...base,
      maxBitrate: ceilingBps,
      burstMaxBitrate: base.burstMaxBitrate !== undefined ? Math.min(base.burstMaxBitrate, ceilingBps) : undefined,
      sdpBitrate: clampSdpBitrateToCeiling(base.sdpBitrate, ceilingBps),
      burstSdpBitrate: clampSdpBitrateToCeiling(base.burstSdpBitrate, ceilingBps),
    };
  }

  /**
   * Pre-warm an RTCPeerConnection with ICE candidate gathering only.
   * No tracks, no transceivers, no signaling, no server-side state.
   * Purely local STUN/TURN binding.
   *
   * Do NOT pre-declare transceivers here. The host is the answerer — the
   * incoming client offer already contains m=audio/m=video, and addTrack()
   * (called in createHostSideConnection() before signal()) will create
   * sendonly transceivers with the actual tracks attached. Pre-declaring
   * empty sendonly transceivers caused JSEP to match them (instead of the
   * track-bearing ones) to the offer's m-sections, producing an
   * `a=inactive` answer and the consistent "blue screen" regression.
   */
  public preWarm(): void {
    this.destroyPreWarmedConnection();
    // Closed over by value so a later pre-warm cannot retarget this connection's transform.
    const binding: SdpBitrateBinding = {};
    this.preWarmSdpBinding = binding;
    const options: PeerConnectionOptions = {
      id: 'pre-warm',
      config: {
        ...this.defaultIceConfig,
        bundlePolicy: 'max-bundle',
        rtcpMuxPolicy: 'require',
        iceCandidatePoolSize: 10,
      },
      batchCandidates: true,
      batchCandidatesTimeout: 1000,
      enableDataChannels: true,
      channelLabel: 'data',
      channelOptions: { priority: 'high' },
      sdpTransform: (sdp) => this.applySdpBitrateTransform(binding, sdp),
    };
    this.preWarmedConnection = this.factory.create(options);
    this.preWarmCreatedAt = Date.now();
    console.log('[PeerConnectionManager] Pre-warmed RTCPeerConnection with iceCandidatePoolSize=10');
  }

  public hasPreWarmedConnection(): boolean {
    return this.preWarmedConnection !== null;
  }

  public destroyPreWarmedConnection(): void {
    if (this.preWarmedConnection) {
      this.preWarmedConnection.destroy();
      this.preWarmedConnection = null;
    }
    this.preWarmSdpBinding = null;
  }

  /**
   * Create a new peer connection for a remote peer (host-side, polite=true).
   * Order: start → addTrack → await preferHardware → burst/settle.
   * Caller must not signal/answer until this promise resolves.
   */
  public async createHostSideConnection(
    peerId: string,
    peerType: PeerType,
    metadata: PeerMetadata,
    videoTrack: MediaStreamTrack | null,
    audioTrack: MediaStreamTrack | null,
  ): Promise<IPeerConnection> {
    const existing = this.connections.get(peerId);
    if (existing) {
      console.log(`[PeerConnectionManager] Destroying stale connection for reconnecting peer: ${peerId}`);
      // Mark as reconnecting so the disconnect callback doesn't fire allPeersDisconnected spuriously.
      this.reconnectingPeerIds.add(peerId);
      // A8: full hygiene (stats poll, recovery state, buffers) — not bare destroy
      this.destroyConnection(peerId);
    }

    const optimizationConfig = configFromMetadata(metadata);

    let connection: IPeerConnection;
    try {
      connection = this.registerHostSideConnection(peerId, peerType, metadata, optimizationConfig);
    } catch (err) {
      this.abandonReconnect(peerId, peerType);
      throw err;
    }

    // The host is the answerer. The client's offer already declares
    // m=audio/m=video, so addTrack() below (BEFORE signal()) creates sendonly
    // transceivers with real tracks + stream associations that JSEP will
    // match cleanly to mid:0/mid:1. Pre-creating empty sendonly transceivers
    // here caused Chrome to match the empty ones first, producing an
    // `a=inactive` answer (the "blue screen" regression).
    this.guardInitialAnswererNegotiation(connection);
    connection.start({ polite: true });

    if (videoTrack) {
      connection.addTrack(videoTrack);
    } else {
      console.warn(`${DIAG} No video track to attach for peer ${peerId} (type=${peerType})`);
      Sentry.addBreadcrumb({
        category: 'BLUE-STREAM-12010',
        message: 'No video track available for peer connection',
        level: 'warning',
        data: { peerId, peerType },
      });
    }
    if (audioTrack) {
      console.log(`${DIAG} Adding audio track to peer ${peerId}`, {
        label: audioTrack.label,
        readyState: audioTrack.readyState,
        enabled: audioTrack.enabled,
        muted: audioTrack.muted,
      });
      connection.addTrack(audioTrack);
      Sentry.addBreadcrumb({
        category: 'AUDIO-BUG-12009',
        message: `Audio track added to peer connection`,
        level: 'info',
        data: { peerId, peerType, label: audioTrack.label, readyState: audioTrack.readyState, enabled: audioTrack.enabled },
      });
    } else {
      console.warn(`${DIAG} No audio track to attach for peer ${peerId} (type=${peerType})`);
      Sentry.addBreadcrumb({
        category: 'AUDIO-BUG-12009',
        message: 'No audio track available for peer connection',
        level: 'warning',
        data: { peerId, peerType },
      });
    }

    const rtcPc = connection.getRtcPeerConnection();
    // Insertable-stream metering takes over the encoded-frame pipe. A dropped pipe
    // silently freezes outbound video (encoderImplementation stays "unknown",
    // packetsSentDelta→0). Admin Connect has no guest latency HUD — skip it.
    if (videoTrack && rtcPc && peerType !== 'admin') this.attachFrameLatencyMeter(peerId, rtcPc);
    const effectiveConfig = this.getEffectiveOptimizationConfig(this.connections.get(peerId)!);
    // Must complete before caller creates/answers SDP (preferHardware race).
    if (rtcPc) {
      const policy = this.mediaPolicyFor(peerId);
      policy.beginConnection(rtcPc, metadata, effectiveConfig);
      await policy.beforeNegotiation(rtcPc);
    }

    // A duplicate offer for the same peerId destroys and replaces this connection while
    // we await codec prefs above. Returning the replaced object would leave the caller
    // signaling a destroyed peer connection, so fail loudly instead: the second offer
    // already owns this peer.
    const currentInfo = this.connections.get(peerId);
    if (!currentInfo || currentInfo.connection !== connection) {
      throw new ConnectionSupersededError(peerId);
    }

    this.beginBurstThenSettleForPeer(peerId, rtcPc, effectiveConfig);

    return connection;
  }

  /** Take (or build) the connection for this peer and register it as the live one. */
  private registerHostSideConnection(
    peerId: string,
    peerType: PeerType,
    metadata: PeerMetadata,
    optimizationConfig: WebRtcOptimizationConfig,
  ): IPeerConnection {
    this.softwareEncodeRecoveryByPeer.set(peerId, new SoftwareEncodeRecoveryController());

    let connection: IPeerConnection;
    if (this.preWarmedConnection && this.isPreWarmHealthy()) {
      console.log('[PeerConnectionManager] Using pre-warmed connection for peer:', peerId);
      connection = this.preWarmedConnection;
      this.preWarmedConnection = null;
      this.adoptPreWarmSdpBindingFor(peerId);
    } else {
      if (this.preWarmedConnection) {
        console.warn('[PeerConnectionManager] Discarding stale/unhealthy pre-warmed connection');
        this.destroyPreWarmedConnection();
      }
      connection = this.factory.create(this.buildConnectionOptions(peerId));
    }

    // After the binding for this peer is known (pre-warm adopted or freshly built), so
    // the first answer SDP is transformed with this peer's own hints.
    const initialConfig = this.mobileSessionConfig.apply(this.withBitrateOverride(optimizationConfig), metadata.isMobile === true,
      this.peerResolution.hasRetainedFloor(peerId) || this.decodeLagDemotionFired.has(peerId));
    this.syncSdpBitrateBinding(peerId, initialConfig, metadata);

    this.wireConnectionEvents(peerId, peerType, metadata, connection);
    this.connections.set(peerId, { peerId, peerType, metadata, connection, optimizationConfig });
    // Clear reconnect flag / hold now that the replacement connection is registered.
    this.clearReconnectHoldTimer(peerId);
    this.reconnectingPeerIds.delete(peerId);
    // Notify coordinator so it can cancel deferred player count changes before ICE completes.
    this.callbacks.onPeerConnectionRegistered?.(peerId, peerType);
    return connection;
  }

  /**
   * Give up on a reconnect whose replacement connection never registered.
   *
   * The stale connection's disconnect was swallowed to keep allPeersDisconnected from
   * firing mid-reconnect. If the replacement then fails to build, nothing else will ever
   * clear that marker, so the session would look permanently "reconnecting" — release it
   * and deliver the disconnect that was suppressed.
   *
   * For player peers the reclaim window is preserved: this is a failed attempt, not a host
   * teardown, so the guest should still be able to re-offer. The disconnect is emitted
   * (matching destroyConnectionForFailedSignal semantics) and armReconnectHold keeps the
   * allPeersDisconnected gate closed for HOST_PLAYER_RECONNECT_HOLD_MS. The mid-reconnect
   * marker stays cleared so UI/host do not treat the failed replacement as still in progress.
   */
  private abandonReconnect(peerId: string, peerType: PeerType): void {
    if (!this.reconnectingPeerIds.delete(peerId)) return;
    if (this.connections.has(peerId)) return;
    console.warn(`[PeerConnectionManager] Replacement connection for ${peerId} failed to register — emitting disconnect`);
    // Arm reclaim hold BEFORE emitting disconnect so the coordinator sees the hold is active.
    // Hold is separate from the mid-reconnect marker: isPeerReconnecting must already be false.
    if (peerType === 'player') {
      this.armReconnectHold(peerId);
    }
    this.callbacks.onPeerDisconnected(peerId, peerType);
  }

  /**
   * Create a client-side connection (impolite=false, single connection).
   * Awaits preferHardware before returning.
   *
   * Tracks are attached BEFORE the codec pass, for the same reason createHostSideConnection
   * does it: the codec pass picks encode-vs-decode capabilities from whether a sender track
   * exists at that moment. Attaching afterwards would make a sending connection negotiate
   * from RECEIVER capabilities and silently skip hardware H.264 profile selection — the
   * exact fault that put a host on OpenH264. With no tracks the connection is recvonly and
   * decode preferences are correct, which is today's behaviour for every caller.
   */
  public async createClientSideConnection(
    peerId: string,
    peerType: PeerType,
    metadata: PeerMetadata,
    videoTrack?: MediaStreamTrack | null,
    audioTrack?: MediaStreamTrack | null,
  ): Promise<IPeerConnection> {
    const options = this.buildConnectionOptions(peerId);
    const connection = this.factory.create(options);
    const optimizationConfig = configFromMetadata(metadata);

    const effectiveConfig = this.withBitrateOverride(optimizationConfig);
    this.syncSdpBitrateBinding(peerId, effectiveConfig, metadata);
    this.softwareEncodeRecoveryByPeer.set(peerId, new SoftwareEncodeRecoveryController());
    this.wireConnectionEvents(peerId, peerType, metadata, connection);
    this.connections.set(peerId, { peerId, peerType, metadata, connection, optimizationConfig });

    // Must precede the codec pass — see the note on this method.
    if (videoTrack) connection.addTrack(videoTrack);
    if (audioTrack) connection.addTrack(audioTrack);

    const rtcPc = connection.getRtcPeerConnection();
    // Same freeze risk as createHostSideConnection — never hijack the encoded pipe for admin.
    if (videoTrack && rtcPc && peerType !== 'admin') this.attachFrameLatencyMeter(peerId, rtcPc);
    // Before start(): start() generates the offer, so codec preferences applied after it
    // would miss the very SDP they exist to shape.
    if (rtcPc) {
      const policy = this.mediaPolicyFor(peerId);
      policy.beginConnection(rtcPc, metadata, effectiveConfig);
      await policy.beforeNegotiation(rtcPc);
    }
    connection.start({ polite: false });
    this.beginBurstThenSettleForPeer(peerId, rtcPc, effectiveConfig);

    return connection;
  }

  private beginBurstThenSettleForPeer(peerId: string, rtcPc: RTCPeerConnection | null, config: WebRtcOptimizationConfig): void {
    this.clearBitratePhaseTimer(peerId);
    // Sticky floor reclaim priming: never burst above the floor (bitrate + fps ceiling).
    if (this.congestionRecovery.restoreStickyFloorHold(peerId)) {
      this.bitratePhaseByPeer.set(peerId, 'settle');
      return;
    }
    const priming = this.congestionRecovery.primingEncodingFor(peerId);
    if (priming.skipBurst && rtcPc) {
      this.bitratePhaseByPeer.set(peerId, 'settle');
      this.optimizationManager.applyVideoEncodingOverrides(rtcPc, {
        maxBitrateBps: priming.maxBitrateBps,
        maxFramerate: priming.maxFramerate,
      });
      return;
    }
    const settledAt = this.pipelineSettledAtMs?.() ?? null;
    if (rtcPc && shouldSkipStartupBurst({ pipelineSettledAtMs: settledAt, nowMs: Date.now() })) {
      this.bitratePhaseByPeer.set(peerId, 'settle');
      this.optimizationManager.applySenderParameters(rtcPc, config);
      return;
    }
    this.bitratePhaseByPeer.set(peerId, 'burst');
    this.optimizationManager.beginStartupBurstThenSettle(rtcPc, config, () => {
      const recoverySnap = this.congestionRecovery.getSnapshot(peerId);
      return recoverySnap.state !== 'normal';
    });
    const timer = setTimeout(() => {
      this.bitratePhaseTimers.delete(peerId);
      if (this.connections.has(peerId)) {
        this.bitratePhaseByPeer.set(peerId, 'settle');
        const snap = this.congestionRecovery.getSnapshot(peerId);
        this.applyRecoveryEncoding(peerId, { maxBitrateBps: snap.maxBitrateBps, maxFramerate: snap.maxFramerate });
      }
    }, STARTUP_BURST_MS);
    this.bitratePhaseTimers.set(peerId, timer);
  }

  /** Supply a Date.now()-domain settle timestamp for new peers; null means capture is not armed. */
  public setPipelineSettledAtProvider(provider: () => number | null): void {
    this.pipelineSettledAtMs = provider;
  }

  private clearBitratePhaseTimer(peerId: string): void {
    const timer = this.bitratePhaseTimers.get(peerId);
    if (timer) {
      clearTimeout(timer);
      this.bitratePhaseTimers.delete(peerId);
    }
    this.bitratePhaseByPeer.delete(peerId);
  }

  public getConnection(peerId: string): IPeerConnection | undefined {
    return this.connections.get(peerId)?.connection;
  }

  public getConnectionInfo(peerId: string): PeerConnectionInfo | undefined {
    return this.connections.get(peerId);
  }

  public getAllConnections(): PeerConnectionInfo[] {
    return Array.from(this.connections.values());
  }

  public getConnectionCount(): number {
    return this.connections.size;
  }

  /** Returns true if the given peer is currently being reconnected (stale connection destroyed, new one pending). */
  public isPeerReconnecting(peerId: string): boolean {
    return this.reconnectingPeerIds.has(peerId);
  }

  /** Returns true if the given peer has an active reconnect hold timer (post-disconnect reclaim window). */
  public hasPeerReconnectHold(peerId: string): boolean {
    return this.reconnectHoldTimers.has(peerId);
  }

  /**
   * True while the session should not be treated as empty: a peer is mid-replacement
   * or inside the post-disconnect reclaim hold window.
   */
  public hasReconnectHold(): boolean {
    return this.reconnectHoldTimers.size > 0 || this.reconnectingPeerIds.size > 0;
  }

  /**
   * True when an incoming offer for this peer re-establishes a connection the host already
   * knows about — the connection is still registered, a replacement is in flight, or the
   * peer is inside its reclaim hold. Callers must read this *before* createHostSideConnection,
   * which registers the replacement connection and clears both the reconnect marker and hold.
   */
  public isReofferFromKnownPeer(peerId: string): boolean {
    return this.connections.has(peerId) || this.reconnectingPeerIds.has(peerId) || this.reconnectHoldTimers.has(peerId);
  }

  /**
   * True when an incoming offer is an in-place ICE restart rather than a full reconnect.
   *
   * An ICE restart offer has the same SDP session ID as the previous offer from this peer,
   * only with new ICE credentials. A full reconnect creates a completely new peer connection
   * with a new session ID. The distinction matters for camera tier decisions: ICE restart
   * should NOT demote the shared camera, since the transport is recovering in place.
   *
   * Must be called BEFORE createHostSideConnection, which destroys the existing connection.
   */
  public isIceRestartOffer(peerId: string, sdp: RTCSessionDescriptionInit): boolean {
    if (!this.connections.has(peerId)) return false;
    const lastSessionId = this.lastOfferSessionIdByPeer.get(peerId);
    if (!lastSessionId) return false;
    const currentSessionId = this.sdpInspector.getSessionId(sdp.sdp);
    if (!currentSessionId) return false;
    return lastSessionId === currentSessionId;
  }

  /**
   * Record the SDP session ID from an incoming offer.
   * Called after processing the offer so subsequent offers can detect ICE restart.
   */
  public recordOfferSessionId(peerId: string, sdp: RTCSessionDescriptionInit): void {
    const sessionId = this.sdpInspector.getSessionId(sdp.sdp);
    if (sessionId) {
      this.lastOfferSessionIdByPeer.set(peerId, sessionId);
    }
  }

  private armReconnectHold(peerId: string): void {
    this.clearReconnectHoldTimer(peerId);
    const timer = setTimeout(() => {
      this.reconnectHoldTimers.delete(peerId);
      // Hold expired unclaimed — sticky session ends for this peer.
      this.clearStickySessionState(peerId);
      this.mediaPolicyByPeer.delete(peerId);
      // Notify for this peer even when another hold remains so generation state cannot leak.
      // The coordinator re-checks aggregate hold/session state before emitting or promoting.
      this.callbacks.onReconnectHoldExpired?.(peerId);
    }, HOST_PLAYER_RECONNECT_HOLD_MS);
    this.reconnectHoldTimers.set(peerId, timer);
    // Notify coordinator so it can reschedule deferred zero player count to match new hold.
    this.callbacks.onReconnectHoldArmed?.(peerId);
  }

  private clearReconnectHoldTimer(peerId: string): void {
    const existing = this.reconnectHoldTimers.get(peerId);
    if (existing) {
      clearTimeout(existing);
      this.reconnectHoldTimers.delete(peerId);
    }
  }

  public async handleRemoteSdp(peerId: string, sdp: RTCSessionDescriptionInit): Promise<void> {
    const conn = this.connections.get(peerId);
    if (!conn) {
      console.warn(`[PeerConnectionManager] No connection found for peer ${peerId}`);
      return;
    }
    // Record guest re-offers so a deferred host ICE-restart can detect that the
    // guest reclaim (single coordinated full re-offer) has superseded it.
    if (sdp.type === 'offer') {
      this.lastRemoteOfferAtByPeer.set(peerId, Date.now());
      this.guestFeedbackPathRestart.noteGuestAlive(peerId);
      this.clearPathRecoverTimer(peerId);
    }
    await conn.connection.signal(sdp);
    this.initialAnswererNegotiation.get(conn.connection)?.();
    this.logRemoteCandidateCount(peerId, conn.connection);
  }

  /** Attaching answerer tracks must not race the incoming initial offer with a local offer. */
  private guardInitialAnswererNegotiation(connection: IPeerConnection): void {
    const rtcPc = connection.getRtcPeerConnection();
    const negotiate = rtcPc?.onnegotiationneeded;
    if (!rtcPc || !negotiate || rtcPc.currentRemoteDescription) return;
    const restoreWhenCommitted = () => {
      if (!rtcPc.currentRemoteDescription) return;
      if (rtcPc.onnegotiationneeded === guarded) rtcPc.onnegotiationneeded = negotiate;
      this.initialAnswererNegotiation.delete(connection);
    };
    const guarded = (event: Event) => {
      restoreWhenCommitted();
      if (!this.initialAnswererNegotiation.has(connection)) negotiate.call(rtcPc, event);
    };
    this.initialAnswererNegotiation.set(connection, restoreWhenCommitted);
    rtcPc.onnegotiationneeded = guarded;
  }

  private logRemoteCandidateCount(peerId: string, connection: IPeerConnection): void {
    IceHealthGuard.getCandidateCounts(connection).then((counts) => {
      if (!counts) return;
      console.log(
        `[PeerConnectionManager] Post-SDP stats for ${peerId}: ${counts.remoteCandidates} remote candidates, ${counts.candidatePairs} candidate pairs`,
      );
      Sentry.addBreadcrumb({
        category: 'webrtc',
        message: `Post-setRemoteDescription candidate stats (${peerId})`,
        level: counts.remoteCandidates === 0 ? 'warning' : 'info',
        data: { peerId, ...counts },
      });
    });
  }

  public async handleRemoteIceCandidate(peerId: string, candidate: RTCIceCandidate): Promise<void> {
    const conn = this.connections.get(peerId);
    const rtcPc = conn?.connection.getRtcPeerConnection();

    // Buffer candidates that arrive before the connection is registered or before
    // setRemoteDescription has resolved. The offer handler may still be awaiting
    // media capture (shadowcam activation, getUserMedia) while ICE trickles in.
    // drainPendingIceCandidates applies them after signal(sdp) completes.
    if (!conn || !rtcPc?.remoteDescription) {
      const pending = this.pendingIceCandidates.get(peerId) ?? [];
      pending.push(candidate);
      this.pendingIceCandidates.set(peerId, pending);
      return;
    }

    await conn.connection.addIceCandidate(candidate);
  }

  /**
   * Apply all ICE candidates that buffered before setRemoteDescription resolved.
   * Must be called after signal(sdp) completes in acceptIncomingPeer so that
   * the remote description is guaranteed to be set.
   *
   * Each candidate is applied independently with its own catch so one bad candidate
   * cannot abort the rest — matches the guest-side PeerCore drain pattern.
   */
  public async drainPendingIceCandidates(peerId: string): Promise<void> {
    const pending = this.pendingIceCandidates.get(peerId);
    if (!pending?.length) return;

    this.pendingIceCandidates.delete(peerId);

    const conn = this.connections.get(peerId);
    if (!conn) return;

    console.log(`[PeerConnectionManager] Draining ${pending.length} buffered ICE candidate(s) for peer ${peerId}`);
    for (const candidate of pending) {
      if (this.connections.get(peerId) !== conn) return;
      try {
        await conn.connection.addIceCandidate(candidate);
      } catch (err) {
        console.warn(`[PeerConnectionManager] addIceCandidate failed for ${peerId}:`, err);
      }
    }
  }

  public sendData(peerId: string, data: string | ArrayBuffer, label?: string): boolean {
    const conn = this.connections.get(peerId);
    if (!conn) {
      console.warn(`[PeerConnectionManager] sendData failed: no connection for ${peerId}`);
      return false;
    }
    const result = conn.connection.send(data, label);
    if (!result) {
      console.warn(`[PeerConnectionManager] sendData failed: send() returned false for ${peerId}`);
    }
    return result;
  }

  public broadcastData(data: string | ArrayBuffer, label?: string): void {
    for (const info of this.connections.values()) {
      if (info.connection.isConnected()) {
        info.connection.send(data, label);
      }
    }
  }

  /**
   * Request a single keyframe from the video encoder for a specific peer.
   * Uses the Chromium-specific second argument to setParameters(); safe on the
   * host (Electron) and a silent no-op if the API is unavailable.
   *
   * Every request passes the KeyframeGate: client-originated ones (`source: 'client'`) are
   * rate-limited by HOST_CLIENT_KEYFRAME_MIN_INTERVAL_MS, and cosmetic reasons
   * (`recovery-climb`, `floor-release`, `loss-burst-end`) are withheld from engines whose
   * jitter-buffer target a keyframe inflates, including Android Chrome. A dropped-frame
   * count does not exempt loss-burst-end. Required reasons bypass the cosmetic gate,
   * but client rate limits still apply.
   */
  public requestKeyframeForPeer(
    peerId: string,
    options?: { source?: KeyframeRequestSource; reason?: KeyframeReason; framesDropped?: number },
  ): void {
    const source = options?.source ?? 'host';
    const reason: KeyframeReason = options?.reason ?? (source === 'client' ? 'client' : 'startup');
    if (reason === 'track-replace') this.keyframeGate.armImmediateRecovery(peerId, 'codec-change');
    if (!this.keyframeGate.admit(peerId, source, reason, options?.framesDropped ?? 0).admit) return;

    const info = this.connections.get(peerId);
    if (!info) return;

    const rtcPc = info.connection.getRtcPeerConnection();
    if (!rtcPc || typeof rtcPc.getSenders !== 'function') return;

    for (const sender of rtcPc.getSenders()) {
      if (sender.track?.kind !== 'video') continue;
      if (!supportsKeyframeRequest(sender)) break;
      // Bound to the capability type so the call resolves against the Chromium two-argument
      // signature rather than lib.dom's single-argument one.
      const keyframeSender: KeyframeCapableSender = sender;
      this.e2eActivePeerId = peerId;
      void this.sendKeyframeRequest(peerId, info.connection, keyframeSender, source, reason,
        options?.framesDropped ?? 0);
      break;
    }
  }

  private async sendKeyframeRequest(peerId: string, connection: IPeerConnection, sender: KeyframeCapableSender,
    source: KeyframeRequestSource, reason: KeyframeReason, framesDropped: number): Promise<void> {
    const captureGeneration = this.captureGenerationByPeer.get(peerId);
    const isCurrent = () => this.connections.get(peerId)?.connection === connection
      && this.captureGenerationByPeer.get(peerId) === captureGeneration;
    for (let attempt = 0; attempt < 2; attempt++) {
      if (!isCurrent()) return;
      try {
        // An overlapping sender update can invalidate getParameters' transaction before
        // this queued operation executes. Retry that race once, with a new transaction.
        const params = sender.getParameters();
        // One keyFrame option per encoding — Chromium matches by index and ignores a
        // shorter array, so a single-element array against a multi-encoding sender is a no-op.
        const encodingOptions = (params.encodings ?? []).map(() => ({ keyFrame: true }));
        await sender.setParameters(params, { encodingOptions });
        if (!isCurrent()) return;
        const idrCount = (this.idrRequestCountByPeer.get(peerId) ?? 0) + 1;
        this.idrRequestCountByPeer.set(peerId, idrCount);
        this.keyframeGate.noteRequested(peerId, source, reason, idrCount, framesDropped);
        return;
      } catch (error) {
        if (attempt === 0 && (error as { name?: string })?.name === 'InvalidStateError' && isCurrent()) continue;
        console.warn(`[PeerConnectionManager] Keyframe request failed for ${peerId}:`, error);
        return;
      }
    }
  }

  /**
   * Replace a track on all active connections (e.g. when MediaCoordinator emits tracksChanged).
   * After each successful replace, re-applies sender encodings and requests an immediate IDR
   * so mid-join shadowcam swaps do not leave a prolonged blurry ramp.
   */
  public async replaceTrackOnAll(oldTrack: MediaStreamTrack, newTrack: MediaStreamTrack,
    context?: VideoTrackSwapContext): Promise<void> {
    if (newTrack.kind !== 'video') {
      await Promise.all([...this.connections.values()].map(info =>
        this.trackReplacementManager.replace(info.connection, oldTrack, newTrack, () => undefined)));
      return;
    }
    const lifecycle = this.swapLifecycle;
    const result = await this.videoTrackSwap.apply({ previousTrack: oldTrack, nextTrack: newTrack,
      requestId: context?.requestId ?? `track-${++this.swapSequence}`,
      deadlineMs: context?.hostDeadlineMs ?? Date.now() + 5000,
      isCurrent: () => lifecycle === this.swapLifecycle && context?.isCurrent?.() !== false,
      commit: context?.commit ?? (() => undefined) });
    if (result.state !== 'applied') throw new Error(result.reason);
  }

  private trackSwapTargets(): TrackSwapTarget[] {
    return [...this.connections.values()].filter(info => info.connection.getRtcPeerConnection()?.getSenders()
      .some(sender => sender.track?.kind === 'video')).map(info => {
      let generation = this.swapGenerations.get(info.connection);
      if (generation === undefined) { generation = ++this.swapSequence; this.swapGenerations.set(info.connection, generation); }
      return { peerId: info.peerId, generation, connection: info.connection };
    });
  }

  /** Recover only the failed connection generation; keep reconnect holds and protective floors. */
  public destroyConnectionForFailedTrackSwap(peerId: string, expectedConnection: IPeerConnection): void {
    if (this.connections.get(peerId)?.connection !== expectedConnection) return;
    this.destroyConnectionForFailedSignal(peerId);
  }

  /** Re-apply effective encodings then request an IDR for one peer connection.
   *  Respects active recovery state to avoid resetting recovery cuts.
   */
  private reprimeSenderParametersAndKeyframe(info: PeerConnectionInfo): void {
    const rtcPc = info.connection.getRtcPeerConnection();
    if (rtcPc && typeof rtcPc.getSenders === 'function') {
      const recoverySnap = this.congestionRecovery.getSnapshot(info.peerId);
      const effectiveConfig = this.getEffectiveOptimizationConfig(info);
      const policy = this.mediaPolicyFor(info.peerId);
      policy.beginConnection(rtcPc, info.metadata, effectiveConfig);
      const videoSender = rtcPc.getSenders().find(sender => sender.track?.kind === 'video');
      const recoveryLimit = recoverySnap.state === 'normal' ? undefined : {
        maxBitrateBps: Math.min(recoverySnap.maxBitrateBps, effectiveConfig.maxBitrate ?? Infinity),
        maxFramerate: Math.min(recoverySnap.maxFramerate, effectiveConfig.targetFrameRate ?? Infinity),
      };
      if (videoSender) {
        void policy.afterTrackReplacement(videoSender, recoveryLimit)
          .then(decision => this.refuseFailedCodecDecision(info.peerId, decision))
          .catch(error => console.warn('[PeerConnectionManager] Track replacement policy failed:', error));
      }
    }
    this.requestKeyframeForPeer(info.peerId, { reason: 'track-replace' });
  }

  private mediaPolicyFor(peerId: string): PeerMediaPolicyLifecycleManager {
    let policy = this.mediaPolicyByPeer.get(peerId);
    if (!policy) {
      policy = new PeerMediaPolicyLifecycleManager({ optimizationManager: this.optimizationManager });
      this.mediaPolicyByPeer.set(peerId, policy);
    }
    return policy;
  }

  public getSignalingGeneration(peerId: string): SignalingGenerationV1 | undefined {
    const policy = this.mediaPolicyByPeer.get(peerId);
    return policy ? { ...policy.bundle.generation } : undefined;
  }

  /** Track-replace backup IDR for every peer (the swap's own request may have beaten the encoder). */
  public requestKeyframeForAllPeers(): void {
    for (const peerId of this.connections.keys()) {
      this.requestKeyframeForPeer(peerId, { reason: 'track-replace' });
    }
  }

  /**
   * WebCodecs pauses Electron video on purpose. The idle-sender repair would otherwise
   * treat that silence as a dead encoder and ICE-restart the connection that carries
   * audio, input, and the native offer.
   */
  public setIdleVideoRepairEnabled(enabled: boolean): void {
    this.idleVideoRepairEnabled = enabled;
    if (!enabled) this.idleVideoSender.clearAll();
  }

  /**
   * Put the live shared capture on this peer's video sender before an answer.
   * A refresh can negotiate a video m-line whose sender has no track; the guest
   * then stays connected and black.
   *
   * Async: callers MUST await before creating the answer, so a failed replacement cannot
   * produce an answer with an empty video sender.
   */
  public async attachLiveCapture(peerId: string, videoTrack: MediaStreamTrack | null): Promise<AttachResult> {
    const connection = this.connections.get(peerId)?.connection;
    const peerConnection = connection?.getRtcPeerConnection();
    if (!connection || !peerConnection) return 'skipped';
    const result = await attachLiveVideoTrack(peerConnection, (track) => connection.addTrack(track), videoTrack);
    if (result === 'replaced' || result === 'added') {
      sinkWebrtcDebugLine(JSON.stringify({
        t: new Date().toISOString(),
        tag: '[Path.recover]',
        peerId,
        action: 'attach-video',
        result,
      }));
    }
    return result;
  }

  /**
   * Attempt an ICE restart for a specific peer. The new offer will flow
   * through the normal 'signal' event → PeerCoordinator → signaling service.
   * Returns false if no connection exists.
   */
  public async tryIceRestart(peerId: string): Promise<boolean> {
    const conn = this.connections.get(peerId);
    if (!conn) return false;
    console.log(`[PeerConnectionManager] Attempting ICE restart for peer: ${peerId}`);
    try {
      await conn.connection.restartIce();
      return true;
    } catch (err) {
      console.error(`[PeerConnectionManager] ICE restart failed for ${peerId}:`, err);
      return false;
    }
  }

  /**
   * Drop every piece of per-peer state except the connection entry itself.
   *
   * Teardown happens from two directions — an explicit destroy and the 'disconnected'
   * event — and each new telemetry map added here has to be cleared on both. Keeping the
   * list in one place is what stops that drift (past misses: lastAnswerSdpByPeer,
   * e2eActivePeerId), so new per-peer state belongs in this method and nowhere else.
   */
  private clearPeerState(peerId: string): void {
    this.codecRefusedPeers.delete(peerId);
    this.codecPinPendingSinceMs.delete(peerId);
    this.captureGenerationByPeer.delete(peerId);
    this.congestionRecovery.disposePeer(peerId);
    this.stopStatsPoll(peerId);
    this.signalQuality.clearPeer(peerId);
    this.frameLatencyTrackers.delete(peerId);
    this.inputPhotonTagger.clearPeer(peerId);
    this.jitterBufferDiagnostics.clearPeer(peerId);
    this.guestEngineByPeer.delete(peerId);
    this.standingLatencyDrain.clearPeer(peerId);
    this.clearBitratePhaseTimer(peerId);
    this.lastPacketsLost.delete(peerId);
    this.lastPacketsSent.delete(peerId);
    this.lastPacketsReceived.delete(peerId);
    this.adminEncodeDeadTicks.delete(peerId);
    this.adminEncodeDeadAlerted.delete(peerId);
    this.lastSendDelaySamples.delete(peerId);
    this.lastHardwareEncodeKind.delete(peerId);
    this.lastHardwareCodecEmitKey.delete(peerId);
    this.lastAnswerSdpByPeer.delete(peerId);
    this.idrRequestCountByPeer.delete(peerId);
    this.lastRttByPeer.delete(peerId);
    this.lastLossPctByPeer.delete(peerId);
    this.keyframeGate.clearPeer(peerId);
    this.softwareEncodeRecoveryByPeer.delete(peerId);
    this.consecutiveSoftwareSamples.delete(peerId);
    this.lastEncodeDiagnosticKey.delete(peerId);
    this.negotiatedVideoCodecByPeer.delete(peerId);
    this.loggedOfferProfiles.delete(peerId);
    this.offeredProfilesByPeer.delete(peerId);
    this.pinSummaryByPeer.delete(peerId);
    this.lastEncoderImplByPeer.delete(peerId);
    this.pendingIceCandidates.delete(peerId);
    this.hostAnswerBuffers.get(peerId)?.cancel();
    this.hostAnswerBuffers.delete(peerId);
    this.sdpBitrateBindings.delete(peerId);
    if (!this.reconnectingPeerIds.has(peerId) && !this.hasPeerReconnectHold(peerId)) {
      this.mediaPolicyByPeer.delete(peerId);
    }
    this.lastRemoteOfferAtByPeer.delete(peerId);
    this.lastOfferSessionIdByPeer.delete(peerId);
    this.guestFeedbackPathRestart.clear(peerId);
    this.idleVideoSender.clear(peerId);
    this.clearPathRecoverTimer(peerId);
    const deferTimer = this.iceRestartDeferTimers.get(peerId);
    if (deferTimer) {
      clearTimeout(deferTimer);
      this.iceRestartDeferTimers.delete(peerId);
    }
    this.clearPlayerIceGrace(peerId);
    if (this.e2eActivePeerId === peerId) this.e2eActivePeerId = undefined;
    const rtcPc = this.connections.get(peerId)?.connection.getRtcPeerConnection();
    this.optimizationManager.cancelStartupBurstSettle(rtcPc ?? null);
    // Deliberately does NOT clear stickyDowngrade / decode-lag episode maps — reclaim and
    // ICE restart must keep the sticky floor and fps ceiling.
  }

  /**
   * Clear sticky floor + decode-lag episode state. ONLY for hold-expiry unclaimed or
   * host-initiated session end — never reclaim / ICE restart.
   */
  private clearStickySessionState(peerId: string): void {
    this.congestionRecovery.clearStickySession(peerId);
    this.decodeLagAppliedFps.delete(peerId);
    this.decodeLagCutBitrateBps.delete(peerId);
    this.decodeLagDemotionFired.delete(peerId);
    this.deliveredFpsByPeer.delete(peerId);
  }

  public destroyConnection(peerId: string): void {
    this.clearPeerState(peerId);
    const conn = this.connections.get(peerId);
    if (conn) {
      // Mark as host-initiated so the 'disconnected' event skips reconnect hold.
      this.hostInitiatedDestroys.add(peerId);
      conn.connection.destroy();
      // If destroy() emitted 'disconnected' synchronously, the handler already ran and
      // removed the registration. If it did not (mock, edge-case), fire the callback here
      // so callers always see exactly one onPeerDisconnected per destroyConnection.
      const handlerRan = !this.connections.has(peerId);
      this.connections.delete(peerId);
      if (!handlerRan) {
        this.hostInitiatedDestroys.delete(peerId);
        this.callbacks.onPeerDisconnected(peerId, conn.peerType);
      }
    }
    // Reclaim replacements set reconnectingPeerIds BEFORE calling destroyConnection — keep
    // the sticky floor on that path. True host-initiated ends clear it here.
    if (!this.reconnectingPeerIds.has(peerId)) {
      this.clearStickySessionState(peerId);
    }
  }

  /**
   * Destroy a connection that failed during signal() — NOT a host-initiated destroy.
   *
   * Unlike destroyConnection(), this does NOT mark the peer as host-initiated, so the
   * 'disconnected' event handler can arm a reconnect hold. A signal failure is a failed
   * attempt that should keep the reclaim window open for the guest to re-offer.
   */
  public destroyConnectionForFailedSignal(peerId: string): void {
    this.clearPeerState(peerId);
    const conn = this.connections.get(peerId);
    if (conn) {
      // Do NOT add to hostInitiatedDestroys — this is not a host-initiated teardown.
      // The disconnect event handler will arm a reconnect hold for player peers.
      conn.connection.destroy();
      this.connections.delete(peerId);
    }
  }

  public destroyAll(): void {
    this.stopWindowWatch();
    this.swapLifecycle++;
    const knownPeerIds = new Set([
      ...this.connections.keys(),
      ...this.statsPollTimers.keys(),
      ...this.bitratePhaseTimers.keys(),
      ...this.hostAnswerBuffers.keys(),
    ]);
    for (const peerId of knownPeerIds) {
      this.clearPeerState(peerId);
      this.clearStickySessionState(peerId);
    }
    this.congestionRecovery.dispose();
    this.stickyDowngrade.clearAll();
    this.qualityLedger.reset();
    this.decodeLagAppliedFps.clear();
    this.decodeLagCutBitrateBps.clear();
    this.decodeLagDemotionFired.clear();
    this.deliveredFpsByPeer.clear();
    for (const timer of this.reconnectHoldTimers.values()) {
      clearTimeout(timer);
    }
    this.reconnectHoldTimers.clear();
    for (const timer of this.iceRestartDeferTimers.values()) {
      clearTimeout(timer);
    }
    this.iceRestartDeferTimers.clear();
    for (const timer of this.pathRecoverTimers.values()) {
      clearTimeout(timer);
    }
    this.pathRecoverTimers.clear();
    this.guestFeedbackPathRestart.clearAll();
    this.idleVideoSender.clearAll();
    for (const timer of this.playerIceGraceTimers.values()) {
      clearTimeout(timer);
    }
    this.playerIceGraceTimers.clear();
    this.lastRemoteOfferAtByPeer.clear();
    this.reconnectingPeerIds.clear();
    this.mediaPolicyByPeer.clear();
    this.destroyPreWarmedConnection();
    // Mark all connections as host-initiated so 'disconnected' skips reconnect hold.
    for (const info of this.connections.values()) {
      this.hostInitiatedDestroys.add(info.peerId);
      info.connection.destroy();
    }
    this.connections.clear();
    this.e2eActivePeerId = undefined;
    this.optimizationManager.dispose();
  }

  /** Handle client congestion datachannel message (legacy clients). */
  public handleCongestionMessage(peerId: string, msg: import('@playcastdotio/PeerCore').CongestionMessage): void {
    this.e2eActivePeerId = peerId;
    this.congestionRecovery.onCongestionMessage(peerId, msg);
  }

  /**
   * Route a guest datachannel health message. Returns true when it was recognised, so the
   * caller knows not to pass it on to the generic type registry.
   */
  public routeClientStatsMessage(peerId: string, parsed: unknown): boolean {
    // The unified stream-health record goes to the observability bus, not the ladder: it is
    // the always-on per-session record, not a congestion measurement. Handled here so a guest
    // speaking it is still counted as an active peer.
    if (isStreamHealthMessage(parsed)) {
      this.streamHealth?.ingestGuestMessage(peerId, parsed);
      this.e2eActivePeerId = peerId;
      return true;
    }
    if (isStandingLatencyDrainResult(parsed)) {
      this.standingLatencyDrain.onDrainResult(peerId, parsed);
      return true;
    }
    const routed = this.congestionRecovery.routeParsedMessage(peerId, parsed);
    if (routed) this.e2eActivePeerId = peerId;
    return routed;
  }

  /**
   * Inject the always-on stream-health bus (the coordinator owns the singleton). Guest
   * streamHealth messages and host decision events flow into it from here on.
   */
  public setStreamHealth(manager: StreamHealthManager): void {
    this.streamHealth = manager;
  }

  /**
   * Mirror budget/floor/sticky decision events into the debug-gated webrtc NDJSON so field
   * replay does not require joining stream-health.ndjson for the latency story. Always-on
   * persistence remains on StreamHealthManager.
   */
  private mirrorHealthEventToWebrtcDebug(peerId: string, event: StreamHealthEvent): void {
    if (!isDebugWebrtcLoggingEnabled()) return;
    if (
      event.kind !== 'latency-floor-over-budget' &&
      event.kind !== 'latency-budget-breach' &&
      event.kind !== 'downgrade'
    ) {
      return;
    }
    const payload = { peerId, kind: event.kind, stream: event.stream, atMs: event.atMs, detail: event.detail };
    console.log('[HostHealth.event]', payload);
    sinkWebrtcDebugLine(
      JSON.stringify({ t: new Date().toISOString(), tag: '[HostHealth.event]', ...payload }),
    );
  }

  /** Guest player input heartbeat for sticky-idle recovery gating. */
  public onPlayerActivity(peerId: string): void {
    this.e2eActivePeerId = peerId;
    this.congestionRecovery.onPlayerActivity(peerId);
  }

  /** Snapshot for Beacon e2e harness (most recently active peer). */
  public getBeaconE2eSnapshot(): HostBeaconE2eSnapshot {
    const peerId = this.e2eActivePeerId ?? [...this.connections.keys()].at(-1);

    if (!peerId) {
      return { role: 'host', recoveryState: 'normal' };
    }

    const recovery = this.congestionRecovery.getSnapshot(peerId);
    const postClear = this.congestionRecovery.getPostClearReadiness(peerId);
    const swState = this.softwareEncodeRecoveryByPeer.get(peerId)?.getState() ?? 'idle';
    return {
      role: 'host',
      rttMs: this.lastRttByPeer.get(peerId),
      packetsLostPct: this.lastLossPctByPeer.get(peerId),
      maxBitrateBps: recovery.maxBitrateBps,
      recoveryState: recovery.state,
      idrRequestCount: this.idrRequestCountByPeer.get(peerId) ?? 0,
      lastAnswerSdp: this.lastAnswerSdpByPeer.get(peerId),
      encodeKind: this.lastHardwareEncodeKind.get(peerId) ?? 'unknown',
      softwareEncodeRecoveryState: swState,
      bitratePhase: this.bitratePhaseByPeer.get(peerId) ?? 'settle',
      recoveryCleared: postClear.recoveryCleared,
      postClearClearedAtMs: postClear.clearedAtMs,
      frameLatencyLastMs: this.getFrameLatencySummary(peerId).lastMs,
      frameLatencyP50Ms: this.getFrameLatencySummary(peerId).p50Ms,
      frameLatencyP95Ms: this.getFrameLatencySummary(peerId).p95Ms,
      frameLatencySampleCount: this.getFrameLatencySummary(peerId).sampleCount,
      calibratedLatencyLastMs: this.getCalibratedLatencySummary(peerId).lastMs,
      calibratedLatencyP50Ms: this.getCalibratedLatencySummary(peerId).p50Ms,
      calibratedLatencyP95Ms: this.getCalibratedLatencySummary(peerId).p95Ms,
      calibratedLatencySampleCount: this.getCalibratedLatencySummary(peerId).sampleCount,
      latencyProbeOffsetTrusted: this.getCalibratedLatencySummary(peerId).offsetTrusted,
      calibratedLatencySource: this.getCalibratedLatencySummary(peerId).source,
      mobileQuality: this.mobileQualitySnapshot(peerId),
    };
  }

  public startCongestionMonitoring(peerId: string): void {
    this.stopStatsPoll(peerId);
    // Silence from here on is evidence of idleness rather than missing evidence.
    this.congestionRecovery.startPeer(peerId);
    const timer = setInterval(() => {
      void this.pollPeerStatsForRecovery(peerId);
    }, PeerConnectionManager.STATS_POLL_MS);
    this.statsPollTimers.set(peerId, timer);
    // Immediate probe
    void this.pollPeerStatsForRecovery(peerId);
  }

  private stopStatsPoll(peerId: string): void {
    const timer = this.statsPollTimers.get(peerId);
    if (timer) {
      clearInterval(timer);
      this.statsPollTimers.delete(peerId);
    }
  }

  /**
   * Highest bitrate this peer's current picture should be given.
   *
   * The profile number is already the steady band for the tier. Passing the live
   * capture and the pinned codec through the band again tightens it when the
   * encoder is HEVC, or when a shared ShadowCam is smaller than this peer's tier.
   */
  private steadyMaxBitrateBps(peerId: string): number {
    const info = this.connections.get(peerId);
    const profile = info ? (this.getEffectiveOptimizationConfig(info).maxBitrate ?? 8_000_000) : 8_000_000;
    return steadySendBandBps(profile, info ? this.encodeContextForPeer(peerId) : undefined).maxBitrateBps;
  }

  /**
   * Congestion recovery encoding (decision 4C): bitrate + fps only at the locked
   * capture resolution, clamping bitrate to the current picture ceiling.
   * applyVideoEncodingOverrides re-pins scaleResolutionDownBy=1; this path must never
   * request a ShadowCam swap or scale tier change.
   */
  private applyRecoveryEncoding(peerId: string, opts: { maxBitrateBps: number; maxFramerate: number }): void {
    const info = this.connections.get(peerId);
    if (!info) return;
    const rtcPc = info.connection.getRtcPeerConnection();
    const maxBitrateBps = Math.min(opts.maxBitrateBps, this.steadyMaxBitrateBps(peerId));
    this.optimizationManager.applyVideoEncodingOverrides(rtcPc, { ...opts, maxBitrateBps });
    console.log(
      `[PeerConnectionManager] Recovery encoding peer=${peerId} maxBitrate=${maxBitrateBps} maxFramerate=${opts.maxFramerate}`,
    );
  }

  /**
   * Hold the existing bitrate on decode lag. Capture resolution and 60 fps stay fixed;
   * the disabled resolution fallback is reported through recovery capabilities.
   */
  private applyDecodeLagTierChange(peerId: string, change: DecodeLagTierChange): void {
    const info = this.connections.get(peerId);
    if (!info) return;
    const rtcPc = info.connection.getRtcPeerConnection();
    if (!rtcPc) return;

    if (this.decodeLagAppliedFps.get(peerId) === TARGET_FRAMERATE) return;

    const profileMax = this.getEffectiveOptimizationConfig(info).maxBitrate ?? 8_000_000;
    const recoverySnap = this.congestionRecovery.getSnapshot(peerId);
    // The 60 → 45 → 30 ladder is disabled. The sender stays at the camera rate.
    // const maxFramerate = Math.min(change.targetFramerate, recoverySnap.maxFramerate);
    const maxFramerate = TARGET_FRAMERATE;
    let cutBitrate = this.decodeLagCutBitrateBps.get(peerId);
    if (cutBitrate === undefined) {
      cutBitrate =
        recoverySnap.state !== 'normal' ? recoverySnap.maxBitrateBps : this.readSenderMaxBitrateBps(rtcPc) ?? profileMax;
      this.decodeLagCutBitrateBps.set(peerId, cutBitrate);
    }
    const senderMax = this.readSenderMaxBitrateBps(rtcPc);
    const maxBitrateBps = Math.min(cutBitrate, senderMax ?? cutBitrate);

    this.optimizationManager.applyVideoEncodingOverrides(rtcPc, {
      maxBitrateBps,
      maxFramerate,
    });

    this.decodeLagAppliedFps.set(peerId, maxFramerate);
    // this.stickyDowngrade.lowerFpsCeiling(peerId, maxFramerate);

    try {
      this.qualityLedger.record(peerId, {
        maxBitrateBps,
        maxFramerate,
        tier: this.currentQualityTierForPeer(peerId),
      });
    } catch {
      // Ledger must never take the encode path down.
    }

    console.log(
      `[PeerConnectionManager] Decode-lag downgrade peer=${peerId} tier=${this.currentQualityTierForPeer(peerId)} fps=${maxFramerate} (${change.framerateReason}) maxBitrate=${maxBitrateBps}`,
    );
  }

  private currentQualityTierForPeer(peerId: string): 'top' | 'middle' | 'bottom' {
    if (this.decodeLagDemotionFired.has(peerId)) return 'bottom';
    const resolution = this.peerResolution.senderDimensions(peerId) ?? this.callbacks.getCaptureResolution?.()
      ?? this.connections.get(peerId)?.optimizationConfig.captureResolution;
    if (!resolution) return 'middle';
    if (resolution.width <= 640 && resolution.height <= 360) return 'bottom';
    if (resolution.width <= 896 && resolution.height <= 480) return 'middle';
    return 'top';
  }

  public mobileQualitySnapshot(peerId: string): NonNullable<HostBeaconE2eSnapshot['mobileQuality']> {
    const recovery = this.congestionRecovery.getMobileQualitySnapshot(peerId);
    const resolution = this.peerResolution.snapshot(peerId);
    return { ...recovery, ...resolution,
      result: recovery.resolutionFallbackUnavailableReason && recovery.decisionAfterQualificationMs !== undefined
        ? 'unavailable' : resolution.result,
      guestEngine: this.guestEngineByPeer.get(peerId) };
  }

  /** Current outbound video sender maxBitrate, if any encodings are present. */
  private readSenderMaxBitrateBps(rtcPc: RTCPeerConnection): number | undefined {
    for (const sender of rtcPc.getSenders()) {
      if (sender.track?.kind !== 'video') continue;
      try {
        const params = sender.getParameters() as RTCRtpSendParameters;
        const bitrate = params.encodings?.[0]?.maxBitrate;
        if (typeof bitrate === 'number' && Number.isFinite(bitrate) && bitrate > 0) {
          return bitrate;
        }
      } catch {
        // getParameters can throw on a mid-teardown sender.
      }
    }
    return undefined;
  }

  /**
   * Apply settle/effective sender params unless recovery is active — then keep recovery overrides.
   */
  private applyEncodingRespectingRecovery(
    peerId: string,
    info: PeerConnectionInfo,
    rtcPc: RTCPeerConnection,
    settleConfig?: WebRtcOptimizationConfig,
  ): void {
    const recoverySnap = this.congestionRecovery.getSnapshot(peerId);
    if (recoverySnap.state !== 'normal') {
      this.optimizationManager.applyVideoEncodingOverrides(rtcPc, {
        maxBitrateBps: recoverySnap.maxBitrateBps,
        maxFramerate: recoverySnap.maxFramerate,
        ...(this.getEffectiveOptimizationConfig(info).temporalScalability
          ? { scalabilityMode: 'L1T3' }
          : {}),
      });
      console.log(
        `[PeerConnectionManager] Recovery encoding peer=${peerId} maxBitrate=${recoverySnap.maxBitrateBps} maxFramerate=${recoverySnap.maxFramerate}`,
      );
      return;
    }
    const effectiveConfig = settleConfig ?? this.getEffectiveOptimizationConfig(info);
    this.optimizationManager.applySenderParameters(rtcPc, effectiveConfig, () => {
      return this.congestionRecovery.getSnapshot(peerId).state !== 'normal';
    });
  }

  /**
   * Send delay accrued by one outbound stream since the previous poll, in ms per frame.
   *
   * Both inputs are lifetime counters, so dividing them directly reports the average
   * since the connection opened and hides a spike that started seconds ago. Returns 0
   * for a stream's first poll and whenever a counter goes backwards (SSRC reset), so a
   * fresh baseline is established instead of a fabricated value.
   */
  /**
     * Attach a per-peer frame-latency meter to the video sender via insertable streams.
     *
     * The host is always Chromium/Electron, so createEncodedStreams is available; a
     * best-effort attach failure must never take the session down, so every failure path
     * here is swallowed with a log.
     */
    private attachFrameLatencyMeter(peerId: string, rtcPc: RTCPeerConnection): void {
      const tracker = new FrameLatencyTracker();
      this.frameLatencyTrackers.set(peerId, tracker);
      const inputPhoton = this.inputPhotonTagger;
      const sendTag = (message: unknown) =>
        this.connections.get(peerId)?.connection.send(JSON.stringify(message));
      try {
        for (const sender of rtcPc.getSenders()) {
          if (sender.track?.kind !== 'video') continue;
          const anySender = sender as RTCRtpSender & {
            createEncodedStreams?: () => { readable: ReadableStream; writable: WritableStream };
          };
          if (typeof anySender.createEncodedStreams !== 'function') continue;
          const { readable, writable } = anySender.createEncodedStreams();
          const transform = new TransformStream({
            transform(frame: { timestamp?: number }, controller: TransformStreamDefaultController) {
              if (typeof frame?.timestamp === 'number') {
                tracker.recordSend(frame.timestamp, Date.now());
                // This is the earliest point at which a frame that could contain the
                // response to a just-injected input has an identity the guest will also
                // see. Enqueue first would be equivalent; tagging here keeps the frame's
                // RTP timestamp and the tag inseparable.
                const tag = inputPhoton.tagFrame(peerId, frame.timestamp);
                if (tag) sendTag(tag);
              }
              controller.enqueue(frame);
            },
          });
          readable.pipeThrough(transform).pipeTo(writable).catch((err: unknown) => {
            // A failed pipe detaches encoded frames from the sender — outbound video freezes.
            console.error(`[PeerConnectionManager] frame-latency meter pipe failed for ${peerId}:`, err);
          });
        }
      } catch (err) {
        console.warn(`[PeerConnectionManager] frame-latency meter attach failed for ${peerId}:`, err);
      }
    }

    /** Guest-reported decode hints, correlated against the host's recorded send times. */
    private onClientFrameDecodeHints(peerId: string, hints: { rtpTimestamp: number; decodeWallMs: number }[]): void {
      const tracker = this.frameLatencyTrackers.get(peerId);
      if (!tracker) return;
      for (const hint of hints) {
        tracker.measureFromDecodeHint(hint);
      }
    }

    /**
     * Record that a sampled guest input was forwarded for injection.
     *
     * The next encoded frame for this peer is tagged with the sequence number, which is
     * what lets the guest close the input-to-photon loop.
     */
    public noteInputForwardedForPhoton(peerId: string, inputSeq: number): void {
      this.inputPhotonTagger.noteInputForwarded(peerId, inputSeq);
    }

    /** Current frame-latency summary for the debug HUD / beacon snapshot. */
    public getFrameLatencySummary(peerId: string): { lastMs: number | null; p50Ms: number | null; p95Ms: number | null; sampleCount: number } {
      const tracker = this.frameLatencyTrackers.get(peerId);
      return tracker?.summary() ?? { lastMs: null, p50Ms: null, p95Ms: null, sampleCount: 0 };
    }

    /**
     * Calibrated glass-to-glass latency for the debug HUD / beacon snapshot.
     * Null before the datachannel handshake has produced a trusted offset.
     */
    public getCalibratedLatencySummary(peerId: string): CalibratedLatencySummary {
      return this.latencyProbe.summary(peerId);
    }

    /**
     * Should this peer's reconnect step the camera down a tier? Read from the link record the
     * stats poll kept, which outlives the connection: demote on capacity evidence (loss, a
     * bandwidth-limited encoder, a critical floor, an RTT spike) or when the peer never
     * carried stats; keep the camera when the link was clean, a standing receive-side buffer
     * included - a smaller picture does not cure that.
     */
    public recoveryDemotionDecision(peerId: string): RecoveryDemotionDecision {
      return this.linkTrouble.decide(peerId);
    }

    /**
     * The guest end of the link for the Beacon debug overlay: codec, jitter buffer, glass-to-
     * glass. Defaults to the most recent connection when no peer is named.
     */
    public getGuestLinkDebugSnapshot(peerId?: string): GuestLinkDebugSnapshot | undefined {
      const id = peerId ?? [...this.connections.keys()].at(-1);
      return id ? this.jitterBufferDiagnostics.guestLink(id) : undefined;
    }

    public handleLatencyProbeMessage(peerId: string, data: string): boolean {
      let parsed: unknown;
      try {
        parsed = JSON.parse(data);
      } catch {
        return false;
      }
      const message = parseLatencyProbeMessage(parsed);
      if (!message) return false;
      return this.congestionRecovery.routeParsedMessage(peerId, message);
    }

    private sendDelaySinceLastPollMs(peerId: string, statId: string, totalSendDelay: number, framesEncoded: number): number {
    let byStat = this.lastSendDelaySamples.get(peerId);
    if (!byStat) {
      byStat = new Map();
      this.lastSendDelaySamples.set(peerId, byStat);
    }

    const previous = byStat.get(statId);
    byStat.set(statId, { totalSendDelay, framesEncoded });
    if (!previous) return 0;

    const deltaDelay = totalSendDelay - previous.totalSendDelay;
    const deltaFrames = framesEncoded - previous.framesEncoded;
    if (deltaDelay < 0 || deltaFrames <= 0) return 0;

    return (deltaDelay / deltaFrames) * 1000;
  }

  private deliveredFpsForPeer(peerId: string): number | null | undefined {
    const native = this.callbacks.getNativeEncodeFps?.(peerId);
    if (native === null) return null;
    if (native !== undefined) return Number.isFinite(native) && native >= 0 ? native : null;
    return this.deliveredFpsByPeer.get(peerId);
  }

  private async pollPeerStatsForRecovery(peerId: string): Promise<void> {
    const info = this.connections.get(peerId);
    if (!info) return;
    const rtcPc = info.connection.getRtcPeerConnection();
    if (!rtcPc || typeof rtcPc.getStats !== 'function') return;

    // Capture the connection reference before the async call
    const connectionAtStart = info.connection;
    const pollStartedAtMs = Date.now();
    let captureGeneration = this.captureGenerationByPeer.get(peerId);
    if (captureGeneration === undefined) {
      captureGeneration = ++this.nextCaptureGeneration;
      this.captureGenerationByPeer.set(peerId, captureGeneration);
    }

    try {
      const report = await rtcPc.getStats();

      // Re-check that the connection still exists and is the same instance after the await.
      // The connection may have been destroyed while getStats() was in-flight.
      const infoAfter = this.connections.get(peerId);
      if (!infoAfter || infoAfter.connection !== connectionAtStart
        || this.captureGenerationByPeer.get(peerId) !== captureGeneration) return;
      let rttMs = 0;
      let rttSampled = false;
      let sendDelayMs = 0;
      let encoderImpl: string | undefined;
      let outboundCodecId: string | undefined;
      const codecById = new Map<string, { mimeType?: string; sdpFmtpLine?: string }>();
      let packetsLost: number | undefined;
      let packetsReceived: number | undefined;
      let packetsSent: number | undefined;
      let targetBitrateBps: number | undefined;
      let encodeFps: number | undefined;
      let qualityLimitationReason: string | undefined;
      let nackCount: number | undefined;
      let pliCount: number | undefined;
      let framesEncoded: number | undefined;
      const videoOutboundIds: string[] = [];

      report.forEach((stat) => {
        // The codec stat names what was actually negotiated — mimeType and profile.
        if (stat.type === 'codec') {
          const entry = stat as { id?: string; mimeType?: string; sdpFmtpLine?: string };
          if (entry.id) {
            codecById.set(entry.id, { mimeType: entry.mimeType, sdpFmtpLine: entry.sdpFmtpLine });
          }
        }
        if (stat.type === 'outbound-rtp' && (stat as { kind?: string }).kind === 'video') {
          videoOutboundIds.push(stat.id);
          const outbound = stat as {
            encoderImplementation?: string;
            codecId?: string;
            totalPacketSendDelay?: number;
            framesEncoded?: number;
            ssrc?: number;
            frameWidth?: number;
            frameHeight?: number;
            packetsSent?: number;
            targetBitrate?: number;
            framesPerSecond?: number;
            qualityLimitationReason?: string;
            nackCount?: number;
            pliCount?: number;
            id?: string;
          };
          encoderImpl = outbound.encoderImplementation;
          outboundCodecId = outbound.codecId;
          if (typeof outbound.packetsSent === 'number') packetsSent = outbound.packetsSent;
          if (typeof outbound.targetBitrate === 'number') targetBitrateBps = outbound.targetBitrate;
          if (typeof outbound.framesEncoded === 'number') framesEncoded = outbound.framesEncoded;
          if (typeof outbound.framesPerSecond === 'number') {
            encodeFps = outbound.framesPerSecond;
            // The rate the guest's decode numbers are judged against. Recorded from the
            // measurement, never from the configured ceiling.
            if (Number.isFinite(encodeFps) && encodeFps > 0) {
              this.deliveredFpsByPeer.set(peerId, encodeFps);
            }
          }
          if (typeof outbound.qualityLimitationReason === 'string') {
            qualityLimitationReason = outbound.qualityLimitationReason;
          }
          if (typeof outbound.nackCount === 'number') nackCount = outbound.nackCount;
          if (typeof outbound.pliCount === 'number') pliCount = outbound.pliCount;
          const delay = outbound.totalPacketSendDelay;
          const frames = outbound.framesEncoded;
          if (typeof delay === 'number' && typeof frames === 'number') {
            const statId = outbound.id ?? 'outbound-video';
            sendDelayMs = Math.max(sendDelayMs, this.sendDelaySinceLastPollMs(peerId, statId, delay, frames));
          }
        }
        if (stat.type === 'remote-inbound-rtp' && (stat as { kind?: string }).kind === 'video') {
          const lost = (stat as { packetsLost?: number }).packetsLost;
          const received = (stat as { packetsReceived?: number }).packetsReceived;
          if (typeof lost === 'number') packetsLost = lost;
          if (typeof received === 'number') packetsReceived = received;
        }
      });

      // The primary Electron sender is paused during native video. Its retained counters
      // and target describe that paused encoder, not the active native media transport.
      const nativeEncodeFps = this.callbacks.getNativeEncodeFps?.(peerId);
      if (nativeEncodeFps !== undefined) {
        encodeFps = nativeEncodeFps ?? undefined;
        encoderImpl = outboundCodecId = undefined;
        packetsLost = packetsReceived = packetsSent = targetBitrateBps = framesEncoded = undefined;
        qualityLimitationReason = undefined; nackCount = pliCount = undefined; sendDelayMs = 0;
      }

      // Diagnostic only — must not feed recovery critical path.
      if (sendDelayMs > 0) {
        console.log(`[PeerConnectionManager] Stats peer=${peerId} rttMs=${rttMs.toFixed(1)} sendDelayMs=${sendDelayMs.toFixed(1)}`);
      }

      let packetsLostDelta: number | undefined;
      if (packetsLost !== undefined) {
        const prevLost = this.lastPacketsLost.get(peerId) ?? packetsLost;
        packetsLostDelta = Math.max(0, packetsLost - prevLost);
        this.lastPacketsLost.set(peerId, packetsLost);
      }
      // If remote-inbound-rtp video is absent this poll, do NOT reset lastPacketsLost to 0.

      let packetsSentDelta = 0;
      if (packetsSent !== undefined) {
        const prevSent = this.lastPacketsSent.get(peerId) ?? packetsSent;
        packetsSentDelta = Math.max(0, packetsSent - prevSent);
        this.lastPacketsSent.set(peerId, packetsSent);
      }

      const selectedPath = this.selectedPathReader.read(report);
      if (selectedPath.rttMs !== undefined) { rttMs = selectedPath.rttMs; rttSampled = true; }

      let packetsReceivedDelta = 0;
      if (packetsReceived !== undefined) {
        const prevReceived = this.lastPacketsReceived.get(peerId) ?? packetsReceived;
        packetsReceivedDelta = Math.max(0, packetsReceived - prevReceived);
        this.lastPacketsReceived.set(peerId, packetsReceived);
      }

      let lossPct: number | undefined;
      if (packetsLostDelta !== undefined && packetsSentDelta > 0) {
        lossPct = Math.min(100, (packetsLostDelta / packetsSentDelta) * 100);
      } else if (packetsLostDelta !== undefined && packetsReceivedDelta + packetsLostDelta > 0) {
        lossPct = Math.min(100, (packetsLostDelta / (packetsReceivedDelta + packetsLostDelta)) * 100);
      } else if (packetsLostDelta !== undefined) {
        lossPct = 0;
      }

      if (lossPct !== undefined) this.lastLossPctByPeer.set(peerId, lossPct);
      this.e2eActivePeerId = peerId;

      // The link record the recovery demotion reads after this peer is gone: was the LINK in
      // trouble (loss, bandwidth limit, RTT spike), or only the receiver's buffer?
      // A hard recovery floor can originate in receiver playback failure, including an idle
      // capture. It is not independent network evidence and must not authorize recapture.
      //
      // latencyFloorExcessMs is the Safari capacity signal: Safari reports no jitter-buffer
      // counters, so the standing detector can never reach a `standing-buffer` verdict there.
      // When the detector has NOT confirmed a buffer but the calibrated g2g floor sits far
      // above the RTT floor, record the excess. A guest that already reported jitter-buffer
      // delay is marked so the demotion rule ignores that excess: on Chromium, wireless loss
      // or a playout-trend spike classifies the same spike as `route` and would otherwise
      // step the camera down on a flat round trip.
      const standingVerdict = this.jitterBufferDiagnostics.currentVerdict(peerId);
      const isStandingBuffer = standingVerdict?.kind === 'standing-buffer';
      const latencyFloorExcessMs =
        !isStandingBuffer && typeof standingVerdict?.excessMs === 'number' && Number.isFinite(standingVerdict.excessMs)
          ? standingVerdict.excessMs
          : undefined;
      const jitterBufferMs = this.jitterBufferDiagnostics.guestLink(peerId)?.jitterBufferMs;
      const jitterBufferReported = typeof jitterBufferMs === 'number' && Number.isFinite(jitterBufferMs);
      this.linkTrouble.record(peerId, {
        lossPct: lossPct ?? 0,
        ...(rttSampled ? { rttMs } : {}),
        qualityLimitationReason: qualityLimitationReason ?? null,
        standingBuffer: isStandingBuffer,
        ...(latencyFloorExcessMs !== undefined ? { latencyFloorExcessMs } : {}),
        ...(jitterBufferReported ? { jitterBufferReported: true } : {}),
      });

      // DEBUG BUILD ONLY — one line per 1Hz host stats poll. Joins local send stats,
      // recovery state, calibrated glass-to-glass (bars + budget), and sticky floor so a
      // single NDJSON line explains why the guest HUD and the encoder are where they are.
      if (isDebugWebrtcLoggingEnabled()) {
        const calibrated = this.getCalibratedLatencySummary(peerId);
        const roundOrNull = (v: number | null): number | null =>
          v === null || !Number.isFinite(v) ? null : Math.round(v);
        const tickPayload = {
          peerId,
          videoStatsSource: nativeEncodeFps === undefined ? 'electron' : 'native',
          rttMs: rttSampled ? Math.round(rttMs) : 'not-sampled',
          rttSampled,
          packetsLostDelta,
          packetsSentDelta,
          lossPct: lossPct === undefined ? 'not-sampled' : Math.round(lossPct * 100) / 100,
          recoveryState: this.congestionRecovery.getSnapshot(peerId).state,
          recoveryMaxBitrateBps: this.congestionRecovery.getSnapshot(peerId).maxBitrateBps,
          stickyFloor: this.stickyDowngrade.floorFor(peerId),
          targetBitrateBps: targetBitrateBps ?? null,
          encodeFps: encodeFps ?? null,
          encoderImplementation: encoderImpl ?? 'unknown',
          selectedPath,
          resolutionStatus: this.peerResolution.status(peerId),
          qualityLimitationReason: qualityLimitationReason ?? null,
          nackCount: nackCount ?? null,
          pliCount: pliCount ?? null,
          calibratedLastMs: roundOrNull(calibrated.lastMs),
          calibratedFloorMs: roundOrNull(calibrated.floorMs),
          calibratedExcessMs: roundOrNull(calibrated.excessMs),
          calibratedSource: calibrated.source,
          latencyProbeOffsetTrusted: calibrated.offsetTrusted,
          latencyBudgetClass: classifyLatencyBudget({
            calibratedMs: calibrated.lastMs ?? undefined,
            floorMs: calibrated.floorMs ?? undefined,
          }),
          signalQualityLevel: this.signalQuality.levelFor(peerId),
          // The host half of input-to-photon: sampled inputs seen, frames tagged in reply.
          photon: this.inputPhotonTagger.countsFor(peerId),
        };
        const tickLine = JSON.stringify({
          t: new Date().toISOString(),
          tag: '[HostStats.tick]',
          ...tickPayload,
        });
        console.log('[HostStats.tick]', tickPayload);
        sinkWebrtcDebugLine(tickLine);
      }

      // Native RTP and WebCodecs both intentionally pause the primary video sender.
      // Its unchanged counters must not restart the audio/input/signaling connection.
      const videoSuspended = rtcPc.getSenders().some((sender) =>
        sender.track?.kind === 'video' && VideoSenderSuspension.holds(sender));
      if (videoSuspended) this.idleVideoSender.clear(peerId);
      else if (this.idleVideoRepairEnabled && this.idleVideoSender.note(peerId, rttSampled, packetsSent, encoderImpl !== undefined)) {
        this.callbacks.onVideoSenderIdle?.(peerId);
      }

      // A poll with no succeeded candidate pair (mid-restart, pair churn) must not feed a
      // fabricated 0ms RTT: the min-filter would pin this peer's floor at zero and every
      // real reading afterwards would count as standing queue — manufacturing hard cuts on
      // a healthy link. Loss still flows; it is not distance-dependent.
      const sourceContext = { outboundId: videoOutboundIds.length === 1 ? videoOutboundIds[0] : '',
        captureGeneration, sampledAtMs: Date.now(), pollStartedAtMs };
      // Admin dashboard has no guest recovery contract — feeding standing-queue soft floors
      // into an encode that already starved (encodeFps null) only re-pins sender params.
      const peerType = this.connections.get(peerId)?.peerType;
      if (peerType === 'admin') {
        this.maybeAlertAdminEncodeDead(peerId, {
          encodeFps: encodeFps ?? null,
          packetsSentDelta,
          encoderImplementation: encoderImpl ?? 'unknown',
          targetBitrateBps: targetBitrateBps ?? null,
          rttMs: rttSampled ? Math.round(rttMs) : null,
          framesEncoded: framesEncoded ?? null,
          videoOutboundCount: videoOutboundIds.length,
          rtcPc,
        });
      }
      if (peerType !== 'admin') {
        if (rttSampled) {
          this.lastRttByPeer.set(peerId, rttMs);
          this.congestionRecovery.onLocalReport(peerId, report, sourceContext, {
            rttMs,
            ...(packetsLostDelta === undefined ? {} : { packetsLostDelta }),
            ...(lossPct === undefined ? {} : { packetsLostPct: lossPct }),
            targetBitrateBps,
            qualityLimitationReason: qualityLimitationReason ?? null,
          });
        } else {
          this.congestionRecovery.onLocalReport(peerId, report, sourceContext, {
            standingQueueMs: null,
            ...(packetsLostDelta === undefined ? {} : { packetsLostDelta }),
            ...(lossPct === undefined ? {} : { packetsLostPct: lossPct }),
            targetBitrateBps,
            qualityLimitationReason: qualityLimitationReason ?? null,
          });
        }
      } else if (rttSampled) {
        this.lastRttByPeer.set(peerId, rttMs);
      }

      if (encoderImpl) {
        const activeCodec = outboundCodecId ? codecById.get(outboundCodecId) : undefined;
        this.logNegotiatedEncode(peerId, encoderImpl, activeCodec);
        await this.handleOutboundEncoderImplementation(peerId, encoderImpl, activeCodec?.sdpFmtpLine);
      }
    } catch (err) {
      console.warn(`[PeerConnectionManager] Stats poll failed for ${peerId}:`, err);
    }
  }

  /**
   * Always-on Admin Connect black-stream detector. After 3 consecutive 1Hz polls with
   * no encode fps and no outbound packets, emit once to console, Sentry, and
   * stream-health.ndjson with track/sender state so one session pins the layer
   * (no outbound vs outbound-but-0-frames vs muted track).
   */
  private maybeAlertAdminEncodeDead(
    peerId: string,
    sample: {
      encodeFps: number | null;
      packetsSentDelta: number;
      encoderImplementation: string;
      targetBitrateBps: number | null;
      rttMs: number | null;
      framesEncoded: number | null;
      videoOutboundCount: number;
      rtcPc: RTCPeerConnection | null;
    },
  ): void {
    const encodeDead =
      (sample.encodeFps === null || sample.encodeFps <= 0) && sample.packetsSentDelta <= 0;
    if (!encodeDead) {
      this.adminEncodeDeadTicks.set(peerId, 0);
      // Allow a later dead episode to alert + re-prime again (still needs 3 consecutive ticks).
      this.adminEncodeDeadAlerted.delete(peerId);
      return;
    }

    const ticks = (this.adminEncodeDeadTicks.get(peerId) ?? 0) + 1;
    this.adminEncodeDeadTicks.set(peerId, ticks);

    const videoSender = sample.rtcPc?.getSenders().find((s) => s.track?.kind === 'video') ?? null;
    const track = videoSender?.track ?? null;
    // DOM lib types getParameters() as RTCRtpParameters (no encodings); senders return RTCRtpSendParameters.
    const encodings = (videoSender?.getParameters() as RTCRtpSendParameters | undefined)?.encodings ?? [];
    const trackState = track
      ? {
          label: track.label,
          readyState: track.readyState,
          muted: track.muted,
          enabled: track.enabled,
          width: track.getSettings().width ?? null,
          height: track.getSettings().height ?? null,
        }
      : null;
    const senderState = {
      hasVideoSender: !!videoSender,
      encodingCount: encodings.length,
      encodingActive: encodings[0]?.active ?? null,
      maxBitrateBps: encodings[0]?.maxBitrate ?? null,
      maxFramerate: encodings[0]?.maxFramerate ?? null,
    };

    // Always-on 1Hz breadcrumb while dead (not debug-gated) — next black session is self-diagnosing.
    sinkStreamHealthLine(
      JSON.stringify({
        t: new Date().toISOString(),
        tag: '[ADMIN_CONNECT]',
        event: 'encode-dead-tick',
        peerId,
        ticks,
        encodeFps: sample.encodeFps,
        packetsSentDelta: sample.packetsSentDelta,
        framesEncoded: sample.framesEncoded,
        videoOutboundCount: sample.videoOutboundCount,
        encoderImplementation: sample.encoderImplementation,
        targetBitrateBps: sample.targetBitrateBps,
        track: trackState,
        sender: senderState,
      }),
    );

    if (ticks < 3 || this.adminEncodeDeadAlerted.has(peerId)) return;

    this.adminEncodeDeadAlerted.add(peerId);
    const layer =
      sample.videoOutboundCount === 0
        ? 'no_outbound_rtp'
        : !track
          ? 'no_video_track_on_sender'
          : track.readyState !== 'live'
            ? 'track_not_live'
            : track.muted
              ? 'track_muted'
              : (sample.framesEncoded ?? 0) === 0
                ? 'outbound_but_zero_frames_encoded'
                : 'outbound_frames_but_no_packets';
    const payload = {
      peerId,
      ticks,
      layer,
      encodeFps: sample.encodeFps,
      packetsSentDelta: sample.packetsSentDelta,
      framesEncoded: sample.framesEncoded,
      videoOutboundCount: sample.videoOutboundCount,
      encoderImplementation: sample.encoderImplementation,
      targetBitrateBps: sample.targetBitrateBps,
      rttMs: sample.rttMs,
      track: trackState,
      sender: senderState,
      hint: 'grep PlayJector-guest.log for [ADMIN_CONNECT] Cam03 publish / setSource outcome= / InitializeCamera',
    };
    console.error('[ADMIN_CONNECT] encode-dead', payload);
    Sentry.addBreadcrumb({
      category: 'ADMIN_CONNECT',
      level: 'error',
      message: `Admin encode-dead (${layer})`,
      data: payload,
    });
    Sentry.captureMessage('[ADMIN_CONNECT] encode-dead', {
      level: 'error',
      tags: { admin_connect: 'encode_dead', admin_encode_layer: layer },
      extra: payload,
    });
    sinkStreamHealthLine(
      JSON.stringify({
        t: new Date().toISOString(),
        tag: '[ADMIN_CONNECT]',
        event: 'encode-dead',
        ...payload,
      }),
    );

    // One-shot recovery: re-prime encodings + request IDR in case the sender never keyed.
    if (track?.readyState === 'live' && !track.muted) {
      this.reprimeEncodingsAfterAnswer(peerId);
      this.requestKeyframeForPeer(peerId, { reason: 'startup' });
      console.warn('[ADMIN_CONNECT] encode-dead recovery: reprime+keyframe', { peerId, layer });
    }
  }

  /** Re-apply encodings after answer and log maxBitrate (reconnect hygiene). */
  /**
   * Pin the peer's SEND codec to a hardware-encodable member of the negotiated set.
   *
   * Runs after negotiation (the negotiated set only exists then) and again as the
   * corrective action when sustained software encode is confirmed — a setParameters call,
   * so unlike the removed ICE-restart escalation it cannot disturb the session.
   *
   * The guest's hardware DECODE capability reaches the pin on the config: this peer's
   * optimizationConfig was built by configFromMetadata from the very PeerMetadata that
   * arrived with the offer, which carries hardwareDecodeFamilies. Threading it there rather
   * than here is deliberate — all three pin contexts (after-answer, after-answer-recovery,
   * sustained-software) go through getEffectiveOptimizationConfig, so none of them can be
   * left behind, and a future pin site gets it for free.
   */
  private pinHardwareSendCodec(
    peerId: string,
    context: string,
  ): void {
    const info = this.connections.get(peerId);
    const rtcPc = info?.connection.getRtcPeerConnection() ?? null;
    if (!info || !rtcPc) return;
    const connectionAtStart = info.connection;
    const config = this.getEffectiveOptimizationConfig(info);
    void this.optimizationManager
      .pinSenderToHardwareCodec(rtcPc, config)
      .then((decision) => {
        // After the async pin, verify the connection still exists and is the same instance.
        // The connection may have been destroyed or replaced (guest reclaim) while the pin
        // was in-flight — writing state for a stale connection would corrupt the new session.
        const infoAfter = this.connections.get(peerId);
        if (!infoAfter || infoAfter.connection !== connectionAtStart) return;

        // The offer was capped for H.264, the conservative guess. The pin knows the
        // codec that is actually encoding, so the sender cap can come down to that
        // picture's rate after startup settles. Active recovery cuts still apply during burst.
        const snap = this.congestionRecovery.getSnapshot(peerId);
        if (this.bitratePhaseByPeer.get(peerId) !== 'burst' || snap.state !== 'normal') {
          this.applyRecoveryEncoding(peerId, { maxBitrateBps: snap.maxBitrateBps, maxFramerate: snap.maxFramerate });
        }

        let summary: string;
        if (!decision) {
          summary = 'no-video-sender';
        } else if (decision.codec) {
          const profile = /profile-level-id=([0-9a-fA-F]+)/.exec(
            decision.codec.sdpFmtpLine ?? '',
          )?.[1];
          const target = profile ?? decision.codec.mimeType;
          summary = 'pinned:' + target;
        } else if (decision.failureReason) {
          summary = decision.failureReason;
        } else if (decision.pinRetryPending) {
          summary = 'sender-pin-pending';
        } else {
          summary = 'no-mutual-hardware-codec';
        }
        this.pinSummaryByPeer.set(peerId, summary + '@' + context);
        console.log('[HW-CODEC] send codec pin', {
          peerId,
          context,
          considered: decision?.considered ?? [],
          hardware: decision?.hardware ?? [],
          guestSoftwareDecode: decision?.guestSoftwareDecode ?? [],
          summary,
        });
        // Persist the structured verdict to the always-on stream-health log so a field
        // session answers "hardware HEVC or software H.264" from disk, no debug build.
        this.streamHealth?.recordHostEvent(peerId, {
          kind: 'send-codec-decision',
          stream: 'video',
          atMs: Date.now(),
          detail: {
            context,
            summary,
            considered: (decision?.considered ?? []).join(','),
            hardware: (decision?.hardware ?? []).join(','),
            guestSoftwareDecode: (decision?.guestSoftwareDecode ?? []).join(','),
            pinned: decision?.codec ? decision.codec.mimeType : '',
            probeUnavailable: decision?.probeUnavailable === true,
            pinAccepted: decision?.pinAccepted === true,
            failureReason: decision?.failureReason ?? '',
          },
        });
        this.refuseFailedCodecDecision(peerId, decision);
        // Push the new pin state to the overlay without waiting for an encode/state change.
        const encode = this.lastHardwareEncodeKind.get(peerId);
        const controllerState = this.softwareEncodeRecoveryByPeer.get(peerId)?.getState();
        if (encode && controllerState) {
          this.emitHardwareCodecStatus(peerId, encode, '', controllerState);
        }
      })
      .catch((err) => console.warn('[HW-CODEC] send codec pin failed:', err));
  }

  private refuseFailedCodecDecision(
    peerId: string,
    decision: SendCodecDecision<RTCRtpCodec> | null,
  ): void {
    if (this.codecRefusedPeers.has(peerId)) return;

    // A retry-pending pin has not answered yet — the sender had no encodings or negotiated
    // codecs. Re-attempt until the window expires; only then is the absence of an answer
    // itself the failure. Refusing immediately would tear down a session that is still
    // negotiating; accepting immediately would let the sender proceed on a software codec.
    if (decision?.pinRetryPending && !decision.failureReason) {
      const pendingSince = this.codecPinPendingSinceMs.get(peerId) ?? Date.now();
      this.codecPinPendingSinceMs.set(peerId, pendingSince);
      if (Date.now() - pendingSince < SENDER_PIN_RETRY_WINDOW_MS) return;
      this.codecRefusedPeers.add(peerId);
      this.codecPinPendingSinceMs.delete(peerId);
      this.callbacks.onSoftwareEncodeRefused?.(peerId, 'sender-pin-pending');
      return;
    }

    if (!decision?.failureReason) return;
    this.codecRefusedPeers.add(peerId);
    this.codecPinPendingSinceMs.delete(peerId);
    // Pass a stable encoder-slot label, not the failure code — callers treat the second
    // argument as an implementation name (e.g. OpenH264), not a policy reason string.
    this.callbacks.onSoftwareEncodeRefused?.(peerId, `pin-failed:${decision.failureReason}`);
  }

  public reprimeEncodingsAfterAnswer(peerId: string): void {
    const info = this.connections.get(peerId);
    if (!info) return;
    const rtcPc = info.connection.getRtcPeerConnection();
    if (!rtcPc) return;
    const effective = this.getEffectiveOptimizationConfig(info);
    this.applyEncodingRespectingRecovery(peerId, info, rtcPc, effective);
    const recoverySnap = this.congestionRecovery.getSnapshot(peerId);
    const maxBitrate = recoverySnap.state !== 'normal' ? recoverySnap.maxBitrateBps : (effective.maxBitrate ?? 0);
    console.log(`[PeerConnectionManager] Re-primed encodings for ${peerId} maxBitrate=${maxBitrate}`);
    this.pinHardwareSendCodec(peerId, 'after-answer');
    this.startCongestionMonitoring(peerId);
  }

  /** Phase-aware reprime: uses burst bitrate during burst phase, settle otherwise.
   *  Also respects active recovery state to avoid resetting recovery cuts.
   */
  private reprimeEncodingsAfterAnswerWithPhase(peerId: string, reapplyHealthySender = false): void {
    const info = this.connections.get(peerId);
    if (!info) return;
    const rtcPc = info.connection.getRtcPeerConnection();
    if (!rtcPc) return;

    // Reclaim / rebuild: restore sticky floor hold before phase-aware burst.
    if (this.congestionRecovery.restoreStickyFloorHold(peerId)) {
      this.startCongestionMonitoring(peerId);
      return;
    }

    const recoverySnap = this.congestionRecovery.getSnapshot(peerId);
    if (recoverySnap.state !== 'normal') {
      this.optimizationManager.applyVideoEncodingOverrides(rtcPc, {
        maxBitrateBps: recoverySnap.maxBitrateBps,
        maxFramerate: recoverySnap.maxFramerate,
      });
      console.log(`[PeerConnectionManager] Re-primed encodings for ${peerId} (recovery active) maxBitrate=${recoverySnap.maxBitrateBps}`);
      this.startCongestionMonitoring(peerId);
      return;
    }

    const effective = this.getEffectiveOptimizationConfig(info);
    const phase = this.bitratePhaseByPeer.get(peerId);
    const maxBitrate = phase === 'burst' ? (effective.burstMaxBitrate ?? effective.maxBitrate ?? 0) : (effective.maxBitrate ?? 0);

    if (reapplyHealthySender && phase !== 'burst') {
      // The negotiated codec is now visible on the sender. Recompute the picture ceiling
      // from it while preserving the current recovery cap and frame-rate state.
      this.applyRecoveryEncoding(peerId, {
        maxBitrateBps: recoverySnap.maxBitrateBps,
        maxFramerate: recoverySnap.maxFramerate,
      });
    }
    console.log(`[PeerConnectionManager] Re-primed encodings for ${peerId} (phase=${phase ?? 'settle'}) maxBitrate=${maxBitrate}`);
    // The shared lifecycle invoked by the connected path owns this healthy-path sender pass.
    // Recovery branches above retain their protective overrides and corrective pin.
    this.startCongestionMonitoring(peerId);
  }

  private recordSdpBreadcrumb(peerId: string, peerType: PeerType, description: RTCSessionDescriptionInit): void {
    const kinds = this.sdpInspector.getMediaKinds(description.sdp);
    const hasVideo = kinds.includes('video');
    Sentry.addBreadcrumb({
      category: 'webrtc',
      message: `Host local SDP (${description.type}) m-sections`,
      level: hasVideo ? 'info' : 'warning',
      data: { peerId, peerType, type: description.type, kinds },
    });
    if (!hasVideo) {
      console.warn(
        `[PeerConnectionManager] Local SDP (${description.type}) for ${peerId} has no m=video — expected sendonly video transceiver.`,
        kinds,
      );
    }
  }

  private isPreWarmHealthy(): boolean {
    if (!this.preWarmedConnection) return false;
    const result = IceHealthGuard.checkPreWarm(this.preWarmedConnection, this.preWarmCreatedAt, PeerConnectionManager.PREWARM_MAX_AGE_MS);
    if (!result.healthy) {
      console.warn(`[PeerConnectionManager] Pre-warm unhealthy: ${result.reason}`);
    }
    return result.healthy;
  }

  /**
   * Software encode is never an acceptable end state.
   *
   * Each software report excludes the H.264 profile the encoder actually chose and
   * renegotiates on the rest; when the escalation budget or the profile list runs out, the
   * session is refused rather than continued behind a warning banner. A software-encoded
   * stream is high-CPU and high-latency in a way that reads to players as a network fault,
   * so shipping one silently is worse than refusing to serve.
   */
  /**
   * One line per change of (negotiated codec, profile, encoder implementation).
   *
   * Without this nobody could tell whether a "software encode" report meant H.264 on the
   * software fallback, or a codec that has no hardware encoder at all. It logs on change
   * rather than per poll so it is readable in a session log.
   */
  private logNegotiatedEncode(
    peerId: string,
    encoderImpl: string,
    codec: { mimeType?: string; sdpFmtpLine?: string } | undefined,
  ): void {
    // One-time: what H.264 profiles did the GUEST actually offer? If the offer carries only
    // the software profile, no host-side ordering can help and the fix must move to the
    // client. This is the fact that distinguishes those two worlds.
    if (!this.loggedOfferProfiles.has(peerId)) {
      const remoteSdp = this.connections.get(peerId)?.connection.getRtcPeerConnection()
        ?.remoteDescription?.sdp;
      if (remoteSdp) {
        this.loggedOfferProfiles.add(peerId);
        const offered = [...new Set(
          [...remoteSdp.matchAll(/profile-level-id=([0-9a-fA-F]+)/g)].map((m) => m[1].toLowerCase()),
        )];
        // H.265 in the offer is the hardware escape for constrained-profile-only guests;
        // record its presence alongside the H.264 profiles so the overlay settles whether
        // the guest offered it at all.
        if (/a=rtpmap:\d+ (H265|HEVC)\//i.test(remoteSdp)) offered.push('h265');
        this.offeredProfilesByPeer.set(peerId, offered);
        console.log('[HW-CODEC] guest offered H.264 profiles', { peerId, offered });
      }
    }

    const key = `${codec?.mimeType ?? '?'}|${codec?.sdpFmtpLine ?? '?'}|${encoderImpl}`;
    if (codec?.mimeType) {
      const profile = extractH264Profile(codec.sdpFmtpLine);
      this.negotiatedVideoCodecByPeer.set(
        peerId,
        profile ? `${codec.mimeType} ${profile}` : codec.mimeType,
      );
    }
    if (this.lastEncodeDiagnosticKey.get(peerId) === key) return;
    this.lastEncodeDiagnosticKey.set(peerId, key);
    console.log('[HW-CODEC] negotiated encode', {
      peerId,
      mimeType: codec?.mimeType ?? 'unknown',
      profile: codec?.sdpFmtpLine ?? 'unknown',
      encoderImplementation: encoderImpl,
      classified: classifyImplementationName(encoderImpl),
    });
  }

  /**
   * Software encode is never an acceptable end state — but `encoderImplementation` is not
   * trustworthy the moment a connection comes up.
   *
   * Chromium wraps hardware encoders in a fallback shim and can report the software name
   * while the hardware encoder is still initialising, so a host with a perfectly good GPU
   * encoder reports OpenH264 for the first seconds of every session. Acting on the first
   * sample — which is what the previous implementation did — turned that into an ICE-restart
   * storm followed by a refused session on hardware that was about to work.
   *
   * So software has to be SUSTAINED across SOFTWARE_ENCODE_CONFIRM_SAMPLES consecutive polls
   * before anything happens, and any hardware report in between resets the count.
   */
  private async handleOutboundEncoderImplementation(
    peerId: string,
    encoderImpl: string,
    _activeFmtpLine?: string,
  ): Promise<void> {
    const encode = classifyImplementationName(encoderImpl);
    this.lastHardwareEncodeKind.set(peerId, encode);

    let controller = this.softwareEncodeRecoveryByPeer.get(peerId);
    if (!controller) {
      controller = new SoftwareEncodeRecoveryController();
      this.softwareEncodeRecoveryByPeer.set(peerId, controller);
    }

    if (encode !== 'software') {
      // Any hardware (or unknown) report clears the streak — the encoder settled.
      this.consecutiveSoftwareSamples.delete(peerId);
      // Let the controller transition from `escalating` → `idle` when non-software is observed.
      controller.onOutboundEncoderImplementation(encoderImpl);
      this.emitHardwareCodecStatus(peerId, encode, encoderImpl, controller.getState());
      return;
    }

    const streak = (this.consecutiveSoftwareSamples.get(peerId) ?? 0) + 1;
    this.consecutiveSoftwareSamples.set(peerId, streak);
    if (streak < SOFTWARE_ENCODE_CONFIRM_SAMPLES) {
      // Still inside the settling window; report nothing and take no action.
      return;
    }
    this.consecutiveSoftwareSamples.set(peerId, 0);

    // Re-attempt the send-codec pin on EVERY confirmed-software window, not only once.
    // The connect-time attempt can fail open while the GPU process is still coming up.
    // A sender that is already producing frames stays active through that re-pin.
    this.pinHardwareSendCodec(peerId, 'sustained-software');

    const action = controller.onOutboundEncoderImplementation(encoderImpl);
    if (action === 'refuse') {
      this.reportHardwareEncodeUnreachable(peerId, encoderImpl);
    }

    this.emitHardwareCodecStatus(peerId, encode, encoderImpl, controller.getState());
  }

  private emitHardwareCodecStatus(
    peerId: string,
    encode: HardwareAccelerationKind,
    encoderImpl: string,
    recoveryState: SoftwareEncodeRecoveryState,
  ): void {
    if (encoderImpl) this.lastEncoderImplByPeer.set(peerId, encoderImpl);
    const impl = this.lastEncoderImplByPeer.get(peerId) ?? encoderImpl;
    const pin = this.pinSummaryByPeer.get(peerId);
    // Keyed on the pin too, so a pin decision refreshes the overlay immediately.
    const emitKey = `${encode}:${recoveryState}:${pin ?? ''}`;
    if (this.lastHardwareCodecEmitKey.get(peerId) === emitKey) return;
    this.lastHardwareCodecEmitKey.set(peerId, emitKey);
    this.callbacks.onHardwareCodecStatus?.(
      peerId,
      {
        encode,
        decode: 'unknown',
        encoderImplementation: impl,
        negotiatedVideoCodec: this.negotiatedVideoCodecByPeer.get(peerId),
        offeredVideoProfiles: this.offeredProfilesByPeer.get(peerId),
        sendCodecPin: pin,
      },
      recoveryState,
    );
  }

  /**
   * One renegotiation attempt toward hardware.
   *
   * This deliberately does NOT exclude the profile the encoder chose. That was the previous
   * design and it is actively harmful: it assumes the profile is the cause, and Chromium
   * lists the hardware-capable profile FIRST, so the profile it excludes on the first
   * software report is the one most likely to be the hardware one. When the real cause is
   * anything else — an encoder still starting, a driver, a concurrent-session limit — that
   * mechanism removes the only good option and guarantees the failure it was meant to fix.
   */

  /**
   * Hardware encode could not be reached for this peer.
   *
   * Strict hardware-only policy makes the refusal terminal after the confirmation window.
   */
  private reportHardwareEncodeUnreachable(peerId: string, encoderImpl: string): void {
    console.error(
      `[PeerConnectionManager] Hardware encode unreachable for ${peerId} (${encoderImpl})`,
      { endingSession: SOFTWARE_ENCODE_POLICY.endSessionWhenHardwareUnreachable },
    );
    if (!SOFTWARE_ENCODE_POLICY.endSessionWhenHardwareUnreachable) return;
    // Share the refuse latch with refuseFailedCodecDecision so a pin failure plus a later
    // software-streak confirmation cannot fire teardown twice for one peer.
    if (this.codecRefusedPeers.has(peerId)) return;
    this.codecRefusedPeers.add(peerId);
    this.callbacks.onSoftwareEncodeRefused?.(peerId, encoderImpl);
  }

  private buildConnectionOptions(peerId: string): PeerConnectionOptions {
    let binding = this.sdpBitrateBindings.get(peerId);
    if (!binding) {
      binding = {};
      this.sdpBitrateBindings.set(peerId, binding);
    }

    return {
      id: peerId,
      config: {
        ...this.defaultIceConfig,
        bundlePolicy: 'max-bundle',
        rtcpMuxPolicy: 'require',
        iceCandidatePoolSize: 10,
      },
      batchCandidates: true,
      batchCandidatesTimeout: 1000,
      enableDataChannels: true,
      channelLabel: 'data',
      channelOptions: { priority: 'high' },
      sdpTransform: (sdp) => this.applySdpBitrateTransform(binding!, sdp),
    };
  }

  /**
   * Hand the pre-warmed connection's SDP binding to the peer consuming it, so the peer's
   * own bitrate hints reach its transform and its answer SDP lands in
   * `lastAnswerSdpByPeer` (which only tracks bindings registered under a peer id).
   */
  private adoptPreWarmSdpBindingFor(peerId: string): void {
    const binding = this.preWarmSdpBinding;
    this.preWarmSdpBinding = null;
    if (!binding) return;
    this.sdpBitrateBindings.set(peerId, binding);
  }

  private syncSdpBitrateBinding(peerId: string, config: WebRtcOptimizationConfig, metadata: PeerMetadata): void {
    let binding = this.sdpBitrateBindings.get(peerId);
    if (!binding) {
      binding = {};
      this.sdpBitrateBindings.set(peerId, binding);
    }
    binding.lowLatencyPlayout = usesLowLatencyPlayout(metadata);
    binding.audioOpusPolicy = usesAudioOpusPolicy(metadata);
    binding.config = sdpTransformConfigFromOptimization(
      config,
      this.sdpBitrateForPhase(peerId, config),
      { lowLatencyPlayout: binding.lowLatencyPlayout, audioOpusPolicy: binding.audioOpusPolicy },
    );
  }

  /**
   * SDP bitrate hints for this peer's current phase.
   *
   * Bandwidth hints are only read while negotiating, so the very first offer/answer has
   * to carry the burst ceiling — publishing settle hints there caps the startup ramp for
   * the whole session even though setParameters is asking for burst. A peer with no
   * recorded phase yet is mid-create, which is exactly that first-SDP case.
   */
  private sdpBitrateForPhase(peerId: string, config: WebRtcOptimizationConfig): SdpBitrateConfig | undefined {
    // An operator bitrate override is a hard ceiling, and only the settle hints are
    // rewritten to honor it.
    if (this.bitrateOverride !== null) return config.sdpBitrate;

    const phase = this.bitratePhaseByPeer.get(peerId) ?? 'burst';
    return phase === 'burst' ? (config.burstSdpBitrate ?? config.sdpBitrate) : config.sdpBitrate;
  }

  /**
   * Every local offer/answer passes through here. The bitrate hints for the peer's phase,
   * then the audio-section rewrites:
   * - The audio section ALWAYS gets its own msid stream id. WebKit pairs audio and video that
   *   share an msid into a sync group and holds the video's playout to NetEq's depth — gating
   *   the split to Chromium reintroduced that on Safari and is why Safari latency regressed
   *   against the 2026-09-23 stream (libs/PeerCore/src/playoutDelay.ts).
   * - The `playout-delay` extmap stays negotiated for every guest: min 0 and a small positive
   *   max select render-ASAP with decode pacing. No engine gate or retransmission buffer.
   * - Opus fmtp/ptime follows usesAudioOpusPolicy: Safari and Chromium get 20 ms packets,
   *   no DTX, and no in-band FEC. Firefox keeps the browser-written audio section.
   */
  private applySdpBitrateTransform(binding: SdpBitrateBinding, sdp: string): string {
    if (!sdp) return sdp;
    const withBitrate = binding.config ? createSdpBitrateTransform(binding.config)(sdp) : sdp;
    const out = separateAudioMsid(withBitrate);
    for (const [peerId, b] of this.sdpBitrateBindings) {
      if (b === binding) {
        this.lastAnswerSdpByPeer.set(peerId, out);
        this.e2eActivePeerId = peerId;
        if (isDebugWebrtcLoggingEnabled()) {
          const line = {
            peerId,
            lowLatencyPlayout: binding.lowLatencyPlayout !== false,
            playoutDelayExtmap: sdpNegotiatesPlayoutDelay(out),
            audioMsidSeparated: out !== withBitrate,
          };
          console.log('[PlayoutDelay] local SDP', line);
          sinkWebrtcDebugLine(JSON.stringify({ t: new Date().toISOString(), tag: '[PlayoutDelay]', ...line }));
        }
        break;
      }
    }
    return out;
  }

  private wireConnectionEvents(peerId: string, peerType: PeerType, metadata: PeerMetadata, connection: IPeerConnection): void {
    let iceConnected = false;
    let channelReady = false;
    let notified = false;
    const connStartTime = Date.now();

    const tryNotifyConnected = () => {
      if (notified || !iceConnected || !channelReady) return;
      notified = true;
      console.log(`[PeerConnectionManager] Peer fully ready: ${peerId} (${peerType}) +${Date.now() - connStartTime}ms`);
      this.callbacks.onPeerConnected(peerId, peerType, metadata);
    };

    connection.on('connected', () => {
      if (this.connections.get(peerId)?.connection !== connection) return;
      console.log(`[PeerConnectionManager] ICE connected: ${peerId} (${peerType}) +${Date.now() - connStartTime}ms`);
      const info = this.connections.get(peerId);
      if (info) {
        const recoverySnap = this.congestionRecovery.getSnapshot(peerId);
        if (recoverySnap.state === 'normal') {
          const effectiveConfig = this.getEffectiveOptimizationConfig(info);
          const phase = this.bitratePhaseByPeer.get(peerId);
          const rtcPc = connection.getRtcPeerConnection();
          if (rtcPc) {
            const negotiatedConfig = phase === 'burst'
              ? { ...effectiveConfig, maxBitrate: effectiveConfig.burstMaxBitrate ?? effectiveConfig.maxBitrate }
              : effectiveConfig;
            const policy = this.mediaPolicyFor(peerId);
            policy.beginConnection(rtcPc, info.metadata, negotiatedConfig);
            void policy.afterNegotiation(rtcPc).then((decision) => {
              this.refuseFailedCodecDecision(peerId, decision);
              // Codec probing/pinning is asynchronous. The peer can settle or enter
              // recovery while it is in flight, so restore the state that is current
              // when the lifecycle pass finishes instead of its startup snapshot.
              this.reprimeEncodingsAfterAnswerWithPhase(peerId, true);
            }).catch((error) => console.warn('[PeerConnectionManager] Post-negotiation policy failed:', error));
          }
        }
      }
      // One startup IDR per connection. Guest first-frame retries cover an encoder
      // that is not ready yet; repeated ICE connected events must not burst IDRs.
      if (!iceConnected) this.requestKeyframeForPeer(peerId);
      iceConnected = true;
      tryNotifyConnected();
    });

    connection.on('channelOpen', () => {
      if (this.connections.get(peerId)?.connection !== connection) return;
      console.log(`[PeerConnectionManager] Data channel open: ${peerId} (${peerType}) +${Date.now() - connStartTime}ms`);
      channelReady = true;
      // The latency probe starts on ICE `connected`, which fires before this, and a
      // (re)opened channel has lost whatever the guest had: re-send the last measured HUD
      // level now that there is a channel. With no reading yet nothing is sent — the guest
      // draws no bars until a measurement exists.
      this.signalQuality.resend(peerId);
      tryNotifyConnected();
    });

    connection.on('disconnected', () => {
      // Ignore stale disconnect from a replaced connection (same peerId, new PC),
      // or from a connection already torn down (no longer registered at all).
      const current = this.connections.get(peerId);
      if (!current) return;
      if (current.connection !== connection) {
        // Clear any stale host-initiated flag so it doesn't leak into future disconnects.
        this.hostInitiatedDestroys.delete(peerId);
        return;
      }
      console.log(`[PeerConnectionManager] Peer disconnected: ${peerId} (${peerType})`);
      // Hold reconnect-eligible so allPeersDisconnected / pavilion restart wait for reclaim.
      // Skip for host-initiated destroys (kick, stream stop) — the guest cannot reclaim.
      const isHostInitiated = this.hostInitiatedDestroys.delete(peerId);
      if (peerType === 'player' && !isHostInitiated) {
        this.armReconnectHold(peerId);
      }
      // A8: clear recovery + stop stats poll even when teardown is event-driven
      this.clearPeerState(peerId);
      this.connections.delete(peerId);
      this.pendingIceCandidates.delete(peerId);
      this.callbacks.onPeerDisconnected(peerId, peerType);
    });

    connection.on('signal', (...args: unknown[]) => {
      if (this.connections.get(peerId)?.connection !== connection) return;
      const description = args[0] as RTCSessionDescriptionInit;
      this.recordSdpBreadcrumb(peerId, peerType, description);
      this.handleGeneratedSignal(peerId, connection, description);
    });

    connection.on('onicecandidates', (...args: unknown[]) => {
      if (this.connections.get(peerId)?.connection !== connection) return;
      const candidates = args[0] as RTCIceCandidate[];
      this.callbacks.onIceCandidatesGenerated(peerId, candidates);
    });

    connection.on('streamRemote', (...args: unknown[]) => {
      if (this.connections.get(peerId)?.connection !== connection) return;
      const stream = args[0] as MediaStream;
      const rtcPc = connection.getRtcPeerConnection();
      if (rtcPc) void this.mediaPolicyFor(peerId).afterReceiverReplacement(rtcPc);
      this.callbacks.onRemoteStream(peerId, stream);
    });

    connection.on('channelData', (...args: unknown[]) => {
      if (this.connections.get(peerId)?.connection !== connection) return;
      const event = args[0] as { data: string | ArrayBuffer; source?: string };
      // peer-lite re-emits every successful send() locally as channelData with
      // source: 'outgoing'. Feeding those back into the inbound pipeline makes
      // every reply we send re-arrive as a phantom request — a failing
      // macro-recorder reply then ping-pongs forever at event-loop speed and
      // starves real inbound messages (input included) off the channel.
      if (event.source === 'outgoing') return;
      this.callbacks.onDataChannelMessage(peerId, event.data);
    });

    connection.on('error', (...args: unknown[]) => {
      if (this.connections.get(peerId)?.connection !== connection) return;
      const error = args[0] as { id: string; message: string };
      console.error(`[PeerConnectionManager] Peer error ${peerId}:`, error.message);
    });

    this.wireIceRestartHandler(connection, peerId, peerType);
  }

  /**
   * Host answers are buffered briefly so useful ICE candidates can bake into
   * the SDP before forwarding. Settles on first relay candidate, gather
   * complete, or HOST_ANSWER_GATHER_TIMEOUT_MS (≤500ms) — whichever first.
   * Non-answer signals forward immediately.
   * Sender params are re-primed here in case encodings were empty at track attach.
   * The startup IDR waits until ICE connects, when media can actually flow.
   */
  private handleGeneratedSignal(peerId: string, connection: IPeerConnection, description: RTCSessionDescriptionInit): void {
    const info = this.connections.get(peerId);
    if (info) {
      const rtcPc = connection.getRtcPeerConnection();
      const effectiveConfig = this.getEffectiveOptimizationConfig(info);
      this.syncSdpBitrateBinding(peerId, effectiveConfig, info.metadata);
      if (rtcPc && typeof rtcPc.getSenders === 'function') {
        this.applyEncodingRespectingRecovery(peerId, info, rtcPc, effectiveConfig);
      }
    }

    if (description.type !== 'answer') {
      this.callbacks.onSignalGenerated(peerId, description);
      return;
    }

    const rtcPc = connection.getRtcPeerConnection();
    if (!rtcPc) {
      this.callbacks.onSignalGenerated(peerId, description);
      return;
    }

    const existing = this.hostAnswerBuffers.get(peerId);
    existing?.cancel();
    const gatherBuffer = new SdpGatherBuffer(PeerConnectionManager.HOST_ANSWER_GATHER_TIMEOUT_MS);
    this.hostAnswerBuffers.set(peerId, gatherBuffer);
    gatherBuffer
      .buffer(rtcPc, description)
      .then((fullSdp) => {
        if (this.hostAnswerBuffers.get(peerId) !== gatherBuffer) return;
        this.hostAnswerBuffers.delete(peerId);
        const candidateCount = (fullSdp.sdp?.match(/a=candidate:/g) ?? []).length;
        console.log(`[PeerConnectionManager] Forwarding host answer for ${peerId}, candidates baked in: ${candidateCount}`);
        this.callbacks.onSignalGenerated(peerId, fullSdp);
      })
      .catch((error) => {
        console.warn(`[PeerConnectionManager] Host answer gather buffering failed for ${peerId}:`, error);
        if (this.hostAnswerBuffers.get(peerId) === gatherBuffer) {
          this.hostAnswerBuffers.delete(peerId);
        }
        this.callbacks.onSignalGenerated(peerId, description);
      });
  }

  private wireIceRestartHandler(connection: IPeerConnection, peerId: string, peerType: PeerType): void {
    const rtcPc = connection.getRtcPeerConnection();
    if (!rtcPc) return;
    let iceEverChecking = false;
    let restarted = false;

    // Take over peer-lite's ICE handler instead of listening alongside it.
    //
    // peer-lite assigns `oniceconnectionstatechange` in its own constructor, and that
    // handler closes the RTCPeerConnection on the first 'disconnected'/'failed'. Because a
    // property handler registered first also FIRES first, everything below used to be dead
    // code: by the time an addEventListener callback ran, the connection was closed and
    // `iceConnectionState` read 'closed', never 'failed', so the guard returned every time.
    // The host consequently never restarted ICE for any peer, never consulted IceHealthGuard
    // and never reported an ICE failure — while the tests covering this path built their own
    // RTCPeerConnection with no peer-lite handler, so they passed regardless.
    //
    // Teardown is still peer-lite's job: the reconnect hold, the reclaim flow and
    // allPeersDisconnected all hang off the 'disconnected' it emits from destroy(). We
    // decide FIRST, then delegate to it for anything we are not recovering in place.
    const peerLiteHandler = rtcPc.oniceconnectionstatechange;
    const delegateTeardown = (event: Event) => peerLiteHandler?.call(rtcPc, event);

    rtcPc.oniceconnectionstatechange = (event: Event) => {
      const state = rtcPc.iceConnectionState;
      if (state === 'checking') iceEverChecking = true;
      if (state === 'connected' || state === 'completed') {
        restarted = false;
        // It came back inside the grace, so nothing was torn down and there is nothing to
        // rebuild. This is what holding on buys: a blip costs zero renegotiation — and,
        // because the demotion is not reported until teardown, zero camera movement too.
        this.clearPlayerIceGrace(peerId);
      }

      // A non-player peer (admin dashboard, remote-host viewer) has no guest-side recovery
      // at all — its ClientPeerManager is constructed without a retry policy — so the host
      // restarting ICE in place is the only thing standing between a transient blip and a
      // permanently dead session. Try that before letting the connection be torn down.
      if (peerType !== 'player' && state === 'disconnected' && iceEverChecking && !restarted) {
        restarted = true;
        console.warn(`[PeerConnectionManager] ICE disconnected for ${peerId} (${peerType}) — in-place restart`);
        void this.tryIceRestart(peerId).then((ok) => {
          if (!ok) delegateTeardown(event);
        });
        return;
      }

      // Hold a player's teardown open while its transport tries to come back, instead of
      // destroying the peer connection the guest is at that very moment trying to repair.
      // See HOST_PLAYER_ICE_GRACE_MS. Teardown itself is still peer-lite's job — this only
      // decides WHEN it is handed over, so the reconnect hold, the reclaim flow and
      // allPeersDisconnected all behave exactly as they did.
      if (peerType === 'player' && state === 'disconnected') {
        this.holdPlayerTeardown(peerId, rtcPc, () => {
          this.reportPlayerTransportTrouble(peerId);
          delegateTeardown(event);
        });
        return;
      }

      if (state !== 'failed') {
        delegateTeardown(event);
        return;
      }

      // The grace was running and the link has now failed outright. Waiting the rest of it out
      // would only delay the reclaim, so hand over to peer-lite now.
      if (this.playerIceGraceTimers.has(peerId)) {
        this.clearPlayerIceGrace(peerId);
        this.reportPlayerTransportTrouble(peerId);
        delegateTeardown(event);
        return;
      }
      // A host restart for this guest peer is already deferred — let the guest
      // reclaim window run rather than re-deciding (which could tear down early).
      if (this.iceRestartDeferTimers.has(peerId)) return;

      const decision = IceHealthGuard.decideIceFailureAction({ iceEverChecking, alreadyRestarted: restarted });
      if (decision.action === 'restart') {
        restarted = true;
        // Phase 2: for guest (player) peers, defer to the guest's single
        // coordinated full re-offer (reclaim) so the host restart does not race
        // it. Other peer types keep the immediate host-restart behaviour.
        if (peerType === 'player') {
          this.deferHostIceRestart(peerId, peerType, rtcPc);
          return;
        }
        console.warn(`[PeerConnectionManager] ICE failed for ${peerId} (${peerType}) — attempting restart`);
        Sentry.addBreadcrumb({
          category: 'webrtc',
          message: `Host-side ICE restart for ${peerId}`,
          level: 'warning',
          data: { peerId, peerType },
        });
        void this.tryIceRestart(peerId).then((ok) => {
          if (!ok && this.getConnection(peerId) === connection) this.destroyConnection(peerId);
        });
      } else {
        console.warn(`[PeerConnectionManager] ICE unrecoverable for ${peerId} (${peerType}): ${decision.action}`);
        this.destroyConnection(peerId);
      }
    };
  }

  /**
   * Keep a player's peer connection alive for HOST_PLAYER_ICE_GRACE_MS after ICE drops.
   *
   * If ICE recovers in that window the grace is cancelled and the session simply continues,
   * with no renegotiation and no keyframe. If it does not, teardown is delegated to peer-lite
   * exactly as it was before, so everything downstream of its 'disconnected' event is
   * unchanged — only its timing moves.
   */
  private holdPlayerTeardown(
    peerId: string,
    rtcPc: RTCPeerConnection,
    tearDown: () => void,
  ): void {
    if (this.playerIceGraceTimers.has(peerId)) return;

    console.warn(
      `[PeerConnectionManager] ICE disconnected for ${peerId} (player) — holding teardown ${PeerConnectionManager.HOST_PLAYER_ICE_GRACE_MS}ms for the guest's own recovery`,
    );

    const timer = setTimeout(() => {
      this.playerIceGraceTimers.delete(peerId);
      if (!this.connections.has(peerId)) return;

      const iceState = rtcPc.iceConnectionState;
      if (iceState === 'connected' || iceState === 'completed') {
        console.log(
          `[PeerConnectionManager] ICE recovered for ${peerId} inside the grace — no teardown`,
        );
        return;
      }
      // An ICE restart is in flight — the guest runs one inside this same window, and its
      // candidates can still be checking when the grace lapses. Tearing down here would kill a
      // negotiation that was about to succeed, and would report a transport failure (which
      // demotes the camera, permanently) for a link that never actually failed. Let it finish:
      // whatever it settles on re-enters this handler, and a real failure still tears down.
      if (iceState === 'checking' || iceState === 'new') {
        console.log(
          `[PeerConnectionManager] ICE still ${iceState} for ${peerId} — letting the restart finish`,
        );
        return;
      }
      console.warn(`[PeerConnectionManager] ICE did not recover for ${peerId} — tearing down`);
      tearDown();
    }, PeerConnectionManager.HOST_PLAYER_ICE_GRACE_MS);

    this.playerIceGraceTimers.set(peerId, timer);
  }

  /**
   * Tell the coordinator a player's transport is really gone, so the shared camera can step
   * down a tier on its behalf.
   *
   * Reported at TEARDOWN, never on the ICE 'disconnected' edge. Once the host started holding
   * a grace open, 'disconnected' stopped meaning "this connection is failing" and started
   * routinely meaning "a blip the grace is about to absorb" — so demoting there moved the
   * shared camera for events that were not failures at all. And because a demotion is never
   * lifted while anything is still connected, that cost was permanent: one absorbed blip and
   * every viewer sat a tier lower for the rest of the session.
   *
   * Waiting for teardown makes the signal mean what the camera move assumes it means. A blip
   * the grace absorbs now moves nothing.
   */
  private reportPlayerTransportTrouble(peerId: string): void {
    const metadata = this.connections.get(peerId)?.metadata;
    if (!metadata) return;
    console.warn(
      `[PeerConnectionManager] Player transport lost for ${peerId} — re-evaluating quality tier`,
    );
    this.callbacks.onPlayerTransportTrouble?.(peerId, metadata);
  }

  private clearPlayerIceGrace(peerId: string): void {
    const timer = this.playerIceGraceTimers.get(peerId);
    if (!timer) return;
    clearTimeout(timer);
    this.playerIceGraceTimers.delete(peerId);
  }

  /**
   * Phase 2 cellular failover: defer the host-side ICE restart for a guest
   * (player) peer so it does not race the guest's single coordinated full
   * re-offer (reclaim).
   *
   * If a guest re-offer lands during the defer window — or ICE recovers on its
   * own — the guest reclaim owns renegotiation and the host restart is skipped.
   * Otherwise the host restart runs as a fallback after the window (still well
   * inside HOST_PLAYER_RECONNECT_HOLD_MS).
   */
  private deferHostIceRestart(peerId: string, peerType: PeerType, rtcPc: RTCPeerConnection): void {
    const failedAt = Date.now();
    const existing = this.iceRestartDeferTimers.get(peerId);
    if (existing) clearTimeout(existing);

    console.warn(
      `[PeerConnectionManager] ICE failed for ${peerId} (${peerType}) — deferring host restart ${PeerConnectionManager.HOST_ICE_RESTART_DEFER_MS}ms for guest reclaim`,
    );
    Sentry.addBreadcrumb({
      category: 'webrtc',
      message: `Deferring host ICE restart for guest reclaim (${peerId})`,
      level: 'warning',
      data: { peerId, peerType },
    });

    const timer = setTimeout(() => {
      this.iceRestartDeferTimers.delete(peerId);
      if (!this.connections.has(peerId)) return;

      const offerAt = this.lastRemoteOfferAtByPeer.get(peerId) ?? 0;
      const guestReclaimed = offerAt > failedAt;
      const iceState = rtcPc.iceConnectionState;
      if (guestReclaimed || iceState === 'connected' || iceState === 'completed') {
        console.log(
          `[PeerConnectionManager] Skipping host ICE restart for ${peerId} — guest reclaim/recovery superseded (ice=${iceState}, guestReclaimed=${guestReclaimed})`,
        );
        return;
      }

      console.warn(`[PeerConnectionManager] No guest reclaim within defer window — host ICE restart for ${peerId}`);
      const metadata = this.connections.get(peerId)?.metadata;
      if (metadata) {
        this.callbacks.onPlayerTransportTrouble?.(peerId, metadata);
      }
      void this.tryIceRestart(peerId).then((ok) => {
        if (!ok) this.teardownFailedPeer(peerId, peerType);
      });
    }, PeerConnectionManager.HOST_ICE_RESTART_DEFER_MS);

    this.iceRestartDeferTimers.set(peerId, timer);
  }

  private teardownFailedPeer(peerId: string, _peerType: PeerType): void {
    this.destroyConnection(peerId);
  }
}

/** Pull the profile-level-id out of an fmtp line for compact display. */
function extractH264Profile(sdpFmtpLine?: string): string | undefined {
  if (!sdpFmtpLine) return undefined;
  return /profile-level-id=([0-9a-fA-F]+)/.exec(sdpFmtpLine)?.[1];
}
